import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Compass as CompassIcon, Plus, Loader2, Scissors, Link2, CornerDownRight,
  HelpCircle, Trash2, Pencil, X, Check, Eye, ArrowRight, Send, Sparkles,
  HelpingHand, Target, Lightbulb, GitBranch,
} from "lucide-react";
import {
  wsGet, wsAddItem, wsPatchItem, wsSeparateItem, wsJoinItems, wsSetSection, wsDeleteItem, wsReorderSiblings,
  wsRegionStart, wsRegionsRestore, wsCoverageGet, wsCoverageConfirm,
  wsAddRel, wsPatchRel, wsDeleteRel, wsCoach, wsRepStatus, wsTurnStatus,
  wsAddQuestion, wsPatchQuestion, wsDeleteQuestion, wsAddQuestionRel,
  wsDeleteQuestionRel, wsThesis,
  wsActivateRegions, wsDeclineRegions, wsThesisRegion, wsClearLatent, wsMakePart,
  wsComposeOpen, wsComposeReturn, wsComposeGet, wsComposeTurn, wantsNewLoop, wsEmergingWhole, wsBigPictureClose, wsIncorporate,
} from "@/lib/api";
import { MindMap } from "@/components/otMindMap";
import { EssayView } from "@/components/EssayView";
import { DoneWriting } from "@/components/DoneWriting";
import { ProgressBlock } from "@/components/ProgressBlock";
import { RefineReply, parseRefineReply } from "@/components/RefineReply";
import { ParagraphFocus } from "@/components/ParagraphFocus";
import { BigPictureWorkspace, useEmergingWhole } from "@/components/EmergingWhole";
import { unitLocation, workingSpan } from "@/lib/paragraphOrder";
import { currentFocusText } from "@/lib/targetSpan";
import { deriveProgress, learnerView, CENTERS, REFINE_INDEX, SET_ASIDE_EVENT, returnEvent, DESTINATION_OF_CENTER, centerIndex } from "@/lib/trajectory";
import OrientationLayer from "@/components/OrientationLayer";
import { difficultyCheck, sameRequestedOperation, OP_REISSUE_LIMIT } from "@/lib/orientation";
import { COACH_WAIT_SLOW_MS, COACH_WAIT_LONG_MS, coachWaitText, coachOutcome, coachFailureText, isDoneSignal,
  TURN_RECOVERY_POLL_MS, TURN_RECOVERY_WINDOW_MS, TURN_RECOVERY_TEXT, TURN_RECOVERY_ACTION, newTurnId } from "@/lib/coachWait";
import { otNoPaste, otOwnCopy } from "@/lib/otPasteGuard";
import { clearDraft, setDraft, unsavedDrafts } from "@/lib/writingDraft";
import { unitEditText } from "@/lib/unitText";
import { intentionalCompose, intentionalEntry, intentionalFocus } from "@/lib/composeIntent";
import { toast } from "sonner";
import { onboardingSignal } from "@/onboarding/signals";
import { ToolsMenu } from "@/components/ToolsMenu";
import { ScratchPad } from "@/components/ScratchPad";

const TERRA = "#8C3A2A";
const canonText = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "").replace(/\s+/g, " ").trim();
// FEI = Foundational / Explicit / Implied. The STUDENT classifies (authorship); TWS teaches
// the distinction only after successful action. "unsure" leaves it unclassified.
const FEI = {
  foundational: { label: "What is taken for granted", short: "P", icon: HelpingHand, cls: "text-amber-700 bg-amber-50 border-amber-300", dot: "bg-amber-500" },
  explicit: { label: "The question being asked", short: "E", icon: Target, cls: "text-[#8C3A2A] bg-[#8C3A2A]/8 border-[#8C3A2A]/30", dot: "bg-[#8C3A2A]" },
  implied: { label: "What is needed to fully answer", short: "I", icon: Lightbulb, cls: "text-teal-700 bg-teal-50 border-teal-300", dot: "bg-teal-500" },
};

// Problem-representation practice copy. ONE evolving workspace; the student works one kind at a
// time. Full conceptual terms only (no E/P/I badges). Presupposed & implied are POSSIBILITIES,
// never required slots — "I don't think I need one" is always a legitimate answer. Reader/answer
// framing: the test is whether you must ANSWER a hidden question for the reader, not merely understand it.
const PHASE_META = {
  explicit: {
    title: "1 · The question being asked",
    prompt: "What question does your assignment actually ask you to answer? If there is more than one, you can add those too.",
    consolidated: "The question the assignment actually asks.",
    placeholder: "The question the assignment actually asks…",
    empty: "What does the assignment actually ask?",
    noun: "question",
  },
  foundational: {
    title: "2 · Questions about what is taken for granted",
    prompt: "What does the assignment assume you understand but that your reader may not? Think of terms, people, events, or context. You decide what your reader needs.",
    consolidated: "Something the assignment takes for granted — that your reader may need.",
    placeholder: "Something your reader may need explained…",
    empty: "",
    noun: "question",
  },
  implied: {
    title: "3 · Questions about what is needed to fully answer",
    prompt: "What else do you need to address to fully answer what the assignment asks? Ask yourself: could you answer what's asked and still leave something important unexplained?",
    consolidated: "A question a complete answer still requires.",
    placeholder: "A question a complete answer still needs…",
    empty: "",
    noun: "question",
  },
};

// OT v1.1 — coordinate the STRUCTURE OF THE PROBLEM (questions) with the STRUCTURE OF THE
// STUDENT'S THINKING (ideas + relationships + thesis). One persistent workspace; the student
// authors everything; TWS grounds, scaffolds, and teaches the operations selectively.
// UDWW: Full Studio has ONE conceptual route — develop a part ⇄ refine that paragraph where it lives (`[Work on this paragraph]`).
// UDWW 3C-4b: ONE writing, two views — STRUCTURE VIEW (the map) / ESSAY VIEW (the backend's document projection of the same units).
// The global Continue-to-Composition control, its readiness gate and the handoff path were REMOVED (not hidden).
export default function ThinkingWorkspace({ sessionId, initialOt, returnPurpose = null, onResume }) {
  const [ot, setOt] = useState(initialOt || null);
  const [items, setItems] = useState([]);
  const [rels, setRels] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [qrels, setQrels] = useState([]);
  const [thesis, setThesis] = useState("");
  const [thesisStatus, setThesisStatus] = useState("developing");
  const [struct, setStruct] = useState({});
  const [loading, setLoading] = useState(true);

  const [thread, setThread] = useState([]);
  const [focusIds, setFocusIds] = useState([]);
  const [, setActiveField] = useState(null); // Active Developmental Field — received for internal focus fallback; no longer displayed (correction #1)
  const [focusQIds, setFocusQIds] = useState([]);
  const [coachBusy, setCoachBusy] = useState(false);
  const [coachWait, setCoachWait] = useState("");   // "" | "slow" | "long" — long-turn status (UI only, no LLM)
  const inflightRef = useRef(0);                      // number of coach turns currently in flight (busy = > 0)
  const awaitingRef = useRef(null);                 // the map action TWS is currently waiting on (for auto-resume)
  const [chatInput, setChatInput] = useState("");
  const threadEndRef = useRef(null);
  const threadRef = useRef([]);
  const chatRef = useRef(null);
  const relPanelRef = useRef(null);
  const [newIdea, setNewIdea] = useState("");
  const [newIsQuestion, setNewIsQuestion] = useState(false);
  const [adding, setAdding] = useState(false);
  const [qDraft, setQDraft] = useState({ explicit: "", foundational: "", implied: "" });
  const [addingQ, setAddingQ] = useState(false);
  const [addOpen, setAddOpen] = useState(null); // fei of a completed section whose add-input is reopened
  const [feiPhase, setFeiPhase] = useState("done"); // Productive Entry (Increment B): no compulsory representation staging
  const [repComplete, setRepComplete] = useState(false); // TWS has judged the representation sufficient
  const [continuePrompt, setContinuePrompt] = useState(false); // one-time "still something to work out?" confirm

  const [editing, setEditing] = useState(null);
  const [separating, setSeparating] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [relDraft, setRelDraft] = useState(null);
  const [grouping, setGrouping] = useState(null);
  const [editingRel, setEditingRel] = useState(null);
  const [editingQ, setEditingQ] = useState(null);
  const [qConnecting, setQConnecting] = useState(null);
  const [linkOpen, setLinkOpen] = useState(null); // idea id whose question-link menu is open

  // UDWW 3C-4b — ONE writing, TWO views: STRUCTURE (the map) / ESSAY (the backend's `document` projection of the SAME units).
  // `doc` is never derived client-side: it is `_document_view(ot)` as returned by ws_get / ws_coach, refetched on each revision.
  // ESSAY-AS-WORKSPACE (consolidated loop): the ESSAY is the default view; STRUCTURE is the other projection of the SAME writing,
  // open to the student whenever their writing exists (a view choice, never a stage).
  const [viewChoice, setViewChoice] = useState(null);
  const [doc, setDoc] = useState(null);
  // PEDAGOGICAL LICENSING ≠ ACCESS: `structureLicensed` governs what TWS TEACHES structurally (instructional affordances in the
  // essay view, orientation) — never whether the student may look at the structure of their own writing.
  const consolidated = ot?.instruction_mode === "consolidated";
  const structureLicensed = !!(ot?.consolidated?.structure_visible || ot?.regions_active || items.some((i) => (i.region || "").trim()));
  const view = viewChoice || (consolidated ? "essay" : "structure");
  const setView = setViewChoice;
  // THE STUDENT'S PROCESS POSITION — per-session VIEW state: where THEY chose to direct attention in the writing process (Move on → /
  // Go back). It counts only while it sits ahead of the controller's centre (lib/trajectory.learnerView); it is not a controller and
  // judges nothing. `refineOpen` derives from it: at "Consolidate each part" every part of the writing offers "Work on this part" — THEY pick.
  const processKey = `compass_process_pos|v2|${sessionId}`;   // v2: the five kinds of work were re-keyed (Understand · Develop · Organize · Refine · Polish)
  const [processPos, setProcessPosState] = useState(() => { try { const v = sessionStorage.getItem(processKey); return v === null ? null : Number(v); } catch { return null; } });
  const setProcessPos = useCallback((v) => {
    setProcessPosState(v);
    try { if (v === null) sessionStorage.removeItem(processKey); else sessionStorage.setItem(processKey, String(v)); } catch { /* ignore */ }
  }, [processKey]);
  const [assignmentOpen, setAssignmentOpen] = useState(true); // salient, persistent assignment panel in OT
  const [developSignal, setDevelopSignal] = useState(0); // ping MindMap to open the add-idea input
  const [highlight, setHighlight] = useState("");   // prompt phrase TWS is directing attention to
  const [lessonDone, setLessonDone] = useState(true); // ATTEMPT-FIRST: the didactic lesson is no longer a mandatory stage; it's available on demand via Help
  const [lessonProgress, setLessonProgress] = useState(0);
  const [lessonHelpOpen, setLessonHelpOpen] = useState(false); // just-in-time "Understanding the Question" lesson overlay
  const [helpOpen, setHelpOpen] = useState(false);     // Stage-3 "How This Works" popup
  const [reviewOpen, setReviewOpen] = useState(false);  // reopen the Stage-1 "Understanding the Question" recap
  const [relatePrompt, setRelatePrompt] = useState(null); // {from_id,to_id} TWS asked the student to relate
  const [demoRelation, setDemoRelation] = useState(null); // v1.2 relation the student already expressed
  const [relInput, setRelInput] = useState(""); // v1.3 inline text for expressing a candidate connection
  const [selectedIdeaId, setSelectedIdeaId] = useState(null); // v1.3 §5: actions are contextual to the selected idea
  const [figureOpen, setFigureOpen] = useState(false); // v1.3 §3: problem representation collapses once done, reopenable
  const [pendingCaptures, setPendingCaptures] = useState([]); // ideas awaiting the student's "keep it"
  const [awaiting, setAwaiting] = useState(null); // REPAIR §3: workspace op TWS is waiting on -> "✓ I've done this"
  const [thesisProposal, setThesisProposal] = useState(null); // REPAIR §2: provisional-thesis candidate awaiting the student's judgment
  // ORIENTATION — proactive difficulty (minimal beta): bare "I don't know"-type sends, or the SAME requested
  // move re-issued twice after the student said "I've done this". Evidence from existing state only.
  const [difficulty, setDifficulty] = useState(null);
  const diffMemRef = useRef({});
  const lastAwaitingRef = useRef(null);
  const selfDoneRef = useRef(false);
  const reissueRef = useRef(0);
  const [regionsActive, setRegionsActive] = useState(false); // FO-unification: the map has acquired essay regions
  const [thesisRegion, setThesisRegion] = useState(""); // FO-unification: where the thesis sits (introduction/body/conclusion)
  const [regionOffer, setRegionOffer] = useState(null); // FO-unification: coach offer to shape thinking as an essay
  const [latentStructure, setLatentStructure] = useState(null); // EARLY EXTERNALIZATION: the whole TWS has externalized (parts + relations); non-blocking, always reshapeable
  const [dialogueCollapsed, setDialogueCollapsed] = useState(false); // usability pass: give the map primacy; dialogue collapsible
  // UDWW Pass 3B — COMPOSE LOCALLY: the focused writing unit (server-owned `ot.compose_focus`), its view (text from the
  // unit; Paragraph Function Map + coaching from the unit's own Composition state), and whether a turn is in flight.
  const [composeFocus, setComposeFocus] = useState(null);
  const [composeView, setComposeView] = useState(null);
  const [composeBusy, setComposeBusy] = useState(false);
  const composeSeenRef = useRef(new Set()); // compose coaching turn_ids already shown in the thread
  const [composeKick, setComposeKick] = useState(0); // CONSOLIDATED LOOP: a coach turn opened / relayed into composition -> refresh the panel

  const lastRevRef = useRef(-1); // Repair 2 — ignore a stale OT response that would revert newer state
  const commitDraftsRef = useRef(null); // WRITING ENTRY: commit open drafts (same save pathway) before a coach turn reads the essay

  const applyOt = useCallback((nextOt, nextStruct) => {
    if (!nextOt) return;
    // Repair 2 — session.ot is authoritative. A response that carries an OLDER revision than what we have
    // already applied (e.g. a coach turn that began before a student map action, or a late map-op response)
    // must NOT overwrite the newer state. Equal/greater revisions apply normally.
    const rev = (typeof nextOt.rev === "number") ? nextOt.rev : null;
    if (rev !== null && lastRevRef.current >= 0 && rev < lastRevRef.current) return;
    if (rev !== null) lastRevRef.current = rev;
    setOt(nextOt);
    const ws = nextOt.workspace || { items: [], relationships: [] };
    setItems(ws.items || []);
    setRels(ws.relationships || []);
    const ps = nextOt.problem_structure || { questions: [], question_relationships: [] };
    setQuestions(ps.questions || []);
    setQrels(ps.question_relationships || []);
    const th = nextOt.thesis || { text: "", status: "developing" };
    setThesis(th.text || "");
    setThesisStatus(th.status || "developing");
    setThesisRegion((th.region || "").toLowerCase());
    setRegionsActive(!!nextOt.regions_active);
    setLatentStructure(nextOt.latent_structure || null);
    setComposeFocus(intentionalFocus(nextOt));   // COMPOSITION AUTHORITY: a focus on a merely recognized paragraph (student_prose) never opens the panel
    if (nextStruct) setStruct(nextStruct);
  }, []);
  const composeShown = useMemo(() => intentionalCompose(ot?.compose), [ot?.compose]);

  useEffect(() => {
    let alive = true;
    wsGet(sessionId)
      .then((r) => {
        if (!alive) return;
        applyOt(r.ot, r.structure);
        setDoc(r.document || null);
        // PRODUCTIVE ENTRY (Increment B): the student lands directly in the thinking workspace — there is
        // NO compulsory "understand the assignment" staging before productive thinking begins. Understanding
        // the assignment/questions remains an important skill, but it is now taught recursively and ON DEMAND
        // (the "Help me understand these questions" overlay), not required as a front gate.
        setFeiPhase("done");
        setRepComplete(true);
        // REPAIR §22: restore the visible developmental dialogue on refresh/reconnect (server-side history),
        // so the student continues where they left off rather than seeing a blank welcome each load.
        const savedDlg = (r.ot && Array.isArray(r.ot.dialogue)) ? r.ot.dialogue.filter((d) => !d.pending) : [];
        savedDlg.forEach((d) => { if (d.turn_id) composeSeenRef.current.add(d.turn_id); });
        if (savedDlg.length) {
          setThread(savedDlg.map((d, i) => ({
            id: `hist-${i}`, role: d.role === "student" ? "student" : "tws", turn_id: d.turn_id || "",
            text: d.text || "", focus: d.focus || [], focusQ: [],
          })));
        } else {
          setThread([{
            id: "welcome", role: "tws", focus: [], focusQ: [],
            text:
              "What are you thinking about this question? Start wherever you want. You can write a sentence, a few ideas, a question, or anything else that helps you get started.",
          }]);
        }
        if (r.ot && r.ot.awaiting_operation) setAwaiting(r.ot.awaiting_operation);
        if (r.ot && r.ot.thesis_proposal) setThesisProposal(r.ot.thesis_proposal);
        if (r.ot && r.ot.region_offer) setRegionOffer(r.ot.region_offer);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [sessionId, applyOt]);

  // A NEW TWS RESPONSE scrolls to its START (the student reads "What you've established" first, not the final question); a student message or
  // a pending state still keeps the end in view. The last TWS message carries a stable ref (`lastTwsRef`).
  const lastTwsRef = useRef(null);
  const lastTwsIdRef = useRef(null);
  useEffect(() => {
    threadRef.current = thread;
    const last = thread[thread.length - 1];
    if (last && last.role === "tws" && last.id !== lastTwsIdRef.current && lastTwsRef.current) {
      lastTwsIdRef.current = last.id;
      lastTwsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (!last || last.role !== "tws") threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [thread, coachBusy]);

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 224) + "px"; // auto-grow up to ~9 lines, then scroll
  }, [chatInput]);

  const pushTws = useCallback((r, opts = {}) => {
    setThread((t) => [...t, {
      id: `tws-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role: "tws", text: r.message, focus: r.focus_item_ids || [], focusQ: r.focus_question_ids || [],
      reflected: !!r.reflected,
    }]);
    const pair = (r.relate_pair && r.relate_pair.length === 2) ? r.relate_pair : null;
    const demo = (r.demonstrated_relation && r.demonstrated_relation.from_id && r.demonstrated_relation.to_id) ? r.demonstrated_relation : null;
    const focusFromRel = demo ? [demo.from_id, demo.to_id] : (pair ? pair : null);
    const af = (r.active_field && (r.active_field.immediate_action || r.active_field.whole_anchor)) ? r.active_field : null;
    setActiveField(af);
    const afFocus = af && (af.active_item_ids || []).length ? af.active_item_ids : null;
    setFocusIds(focusFromRel ? focusFromRel : (r.focus_item_ids && r.focus_item_ids.length ? r.focus_item_ids : (afFocus || [])));
    setFocusQIds(r.focus_question_ids || []);
    setHighlight(r.highlight || "");
    if (r.structure) setStruct(r.structure);
    setAwaiting(r.awaiting_operation || null); // REPAIR §3: show/hide "✓ I've done this"
    setThesisProposal(r.thesis_proposal || null); // REPAIR §2: show/hide provisional-thesis scaffold
    setRegionOffer(r.region_offer || null); // FO-unification: show/hide the shape-as-essay offer
    // v1.2: demonstrated relation takes priority (representation catches up to thinking); else candidate pair.
    setDemoRelation(demo);
    setRelatePrompt(!demo && pair ? { from_id: pair[0], to_id: pair[1] } : null);
    if (focusFromRel) requestAnimationFrame(() => relPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));
    if (!opts.skipCapture && r.capture_pending && r.capture_pending.length) {
      setPendingCaptures((prev) => [...prev, ...r.capture_pending]);
    }
    if (r.phase_advance) {
      setFeiPhase((p) => (p === "explicit" ? "foundational" : p === "foundational" ? "implied" : p === "implied" ? "done" : p));
    }
    // v1.3.1: the coach may conclude problem representation is sufficient from ANY phase (FEI categories
    // are not required slots) — finish the agenda without forcing empty foundational/implied entries.
    if (r.representation_complete) {
      setFeiPhase("done");
      setRepComplete(true);
      wsRepStatus(sessionId, "tws_sufficient").catch(() => {});
    }
  }, [sessionId]);

  // STUDENT-CONTROLLED PROGRESSION: the student may always continue into substantive OT. If TWS still
  // judges the representation unresolved, show ONE lightweight confirmation; if TWS already judged it
  // sufficient, continue silently. Continuing carries NO penalty — it is process history only.
  const proceedToThinking = useCallback((status) => {
    setContinuePrompt(false);
    setFeiPhase("done");
    if (status) wsRepStatus(sessionId, status).catch(() => {});
    // Orient the student: the task changes from "what do I have to figure out?" to "working out what I think".
    setThread((t) => [...t, {
      id: `orient-${Date.now()}`, role: "tws", focus: [], focusQ: [],
      text: "You've worked out what you need to answer. Now develop the ideas you'll need to answer those questions — you're not writing the paper yet, you're working out what you think. You can start wherever it makes sense: choose a question to work on, or add an idea you already have. You can reopen and change these questions anytime.",
    }]);
  }, [sessionId]);

  const requestContinue = useCallback(() => {
    if (repComplete) { proceedToThinking("tws_sufficient"); return; }
    setContinuePrompt(true);
  }, [repComplete, proceedToThinking]);

  const turnArgsRef = useRef({});                     // AUTHENTIC REPAIR 1: turn_id -> the exact request (for recovery / same-id resend)
  const pendingTurnRef = useRef(null);                // the last turn that ended unresolved: {turnId, message}

  const bigPictureAttentionRef = useRef(() => {});          // filled once the Emerging Whole hook exists below (TWS → Big Picture invocation seam)
  const bigPictureOpenRef = useRef(false);                  // navigation state sent with each turn (WHOLE ORIENTATION: is the Big Picture open?)
  const applyCoachResult = useCallback((r) => {
    // INVARIANT: a successful coach response ALWAYS applies the authoritative returned workspace. The client
    // must never show a coach message describing a map change while withholding the state that carries it
    // (pasted student text is student input like typed text — it is captured and organized the same way).
    if (r.ot) applyOt(r.ot, r.structure);
    if (r.document) setDoc(r.document);
    pushTws(r);
    if (r.compose_turn && r.compose_turn.unit_id) setComposeKick((k) => k + 1);   // the engine's reading arrives through the compose panel's poll
    if (r.consolidated && r.consolidated.whole_orientation) bigPictureAttentionRef.current({ ...r.consolidated.whole_orientation, coach: r.message || "" });   // WHOLE ORIENTATION signal {level, reason, key, message} — level 2 opens the Big Picture once (presentation only), the coach's words shown beside Next
    if (r.consolidated) onboardingSignal("coach_result", { sessionId, level: (r.consolidated.whole_orientation || {}).level || 0, operation: r.consolidated.operation || "", structure_visible: !!r.consolidated.structure_visible });   // presentation-only seam for just-in-time introductions
  }, [applyOt, pushTws, sessionId]);

  // TIMEOUT RECOVERY — poll the read-only turn status for the SAME turn_id. Resolves with the server's last word:
  // done (the persisted result) | processing (window expired, still running) | not_run (safe to resend, same id).
  const recoverTurn = useCallback(async (turnId, windowMs) => {
    const started = Date.now();
    let last = null;
    for (;;) {
      try { last = await wsTurnStatus(sessionId, turnId); } catch (e) { /* a blip while polling: keep polling */ }
      if (last && (last.status === "done" || last.status === "not_run")) return last;
      if (Date.now() - started >= windowMs) return last || { status: "processing", turn_id: turnId };
      await new Promise((res) => setTimeout(res, TURN_RECOVERY_POLL_MS));
    }
  }, [sessionId]);

  const pushRecoveryNote = useCallback((turnId, status) => {
    pendingTurnRef.current = { turnId, message: (turnArgsRef.current[turnId] || {}).message || "" };
    setThread((t) => [...t, { id: `recover-${turnId}-${Date.now()}`, role: "tws", focus: [], focusQ: [],
      text: TURN_RECOVERY_TEXT[status], recovery: { turnId, status } }]);
  }, []);

  const runCoach = useCallback(async (message = "", event = "", phase = "", extra = {}) => {
    // BUSY STATE = "a coach turn is in flight" — counted per turn, so an overlapping turn (a map move while a reply
    // is still coming back) can neither hide a running turn's loader nor leave a stale one behind after usable
    // feedback is on screen. Status timers belong to THIS turn and are cleared by THIS turn.
    const turnId = extra.turn_id || newTurnId();
    const sendExtra = { ...extra, turn_id: turnId, big_picture_open: !!bigPictureOpenRef.current };
    turnArgsRef.current[turnId] = { message, event, phase, extra: sendExtra };
    if (extra.turn_id) setThread((t) => t.filter((m) => !(m.recovery && m.recovery.turnId === turnId)));   // a resend closes its note
    // UNSAVED WRITING REACHES TWS: an open, changed draft is committed through the same save before TWS reads the essay (never a resend)
    if (!extra.turn_id && commitDraftsRef.current) { try { await commitDraftsRef.current(); } catch { /* the draft stays in its editor */ } }
    inflightRef.current += 1;
    setCoachBusy(true);
    setCoachWait("");
    const myTimers = [
      setTimeout(() => setCoachWait("slow"), COACH_WAIT_SLOW_MS),
      setTimeout(() => setCoachWait("long"), COACH_WAIT_LONG_MS),
    ];
    try {
      let r = null;
      try {
        r = await wsCoach(sessionId, message, event, phase, sendExtra);
      } catch (e1) {
        const lost = coachOutcome(e1) === "timeout" || e1?.response?.status === 409;   // response lost / turn already running
        if (!lost) {
          await new Promise((res) => setTimeout(res, 700)); // one silent retry (SAME turn_id: a completed turn replays, never re-runs)
          r = await wsCoach(sessionId, message, event, phase, sendExtra);
        }
      }
      if (r === null) {
        // TIMEOUT ≠ FAILED TURN: recover the same turn; never resubmit it.
        myTimers.forEach(clearTimeout);
        setCoachWait("recovering");
        const s = await recoverTurn(turnId, TURN_RECOVERY_WINDOW_MS);
        if (s.status === "done") r = s;
        else { pushRecoveryNote(turnId, s.status === "not_run" ? "not_run" : "processing"); return s.status === "not_run" ? false : null; }
      }
      if (pendingTurnRef.current && pendingTurnRef.current.turnId === turnId) pendingTurnRef.current = null;
      applyCoachResult(r);
      if (event === "operation_done") {
        const again = selfDoneRef.current && sameRequestedOperation(lastAwaitingRef.current, r.awaiting_operation);
        reissueRef.current = again ? reissueRef.current + 1 : 0;
        selfDoneRef.current = false;
        if (reissueRef.current >= OP_REISSUE_LIMIT) { reissueRef.current = 0; setDifficulty({ kind: "op_repeat", at: Date.now() }); }
      }
      return true;
    }
    catch (e) {
      // REPAIR 10 / repair #4 — this catch is a TECHNICAL/request failure (the coach call threw, even after a silent
      // retry), NOT the student failing to communicate. Never blame their input; reassure that their work is
      // preserved and invite a resend (which reuses this turn_id, so a turn that did complete is replayed).
      const outcome = coachOutcome(e);
      const last = threadRef.current && threadRef.current.length ? threadRef.current[threadRef.current.length - 1] : null;
      const repeated = !!(last && last.role === "tws" && /wasn't able to process|trouble on my end|timed out/i.test(last.text || ""));
      pendingTurnRef.current = { turnId, message };
      pushTws({ message: coachFailureText(outcome, repeated), focus_item_ids: [], outcome });
      return false;
    }
    finally {
      myTimers.forEach(clearTimeout);
      inflightRef.current = Math.max(0, inflightRef.current - 1);
      if (inflightRef.current === 0) { setCoachWait(""); setCoachBusy(false); }
    }
  }, [sessionId, pushTws, applyCoachResult, recoverTurn, pushRecoveryNote]);

  // The student asks whether a still-processing turn has finished (one status check — a short poll — never a resend),
  // or resends a turn the server confirmed never ran (same turn_id).
  const checkRecovery = useCallback(async (rec) => {
    const args = turnArgsRef.current[rec.turnId];
    setThread((t) => t.filter((m) => !(m.recovery && m.recovery.turnId === rec.turnId)));
    if (rec.status === "not_run") {
      setChatInput((v) => (args && v === args.message ? "" : v));   // the restored text is being sent from here
      if (args) await runCoach(args.message, args.event, args.phase, args.extra);
      return;
    }
    inflightRef.current += 1; setCoachBusy(true); setCoachWait("recovering");
    try {
      const s = await recoverTurn(rec.turnId, TURN_RECOVERY_POLL_MS * 3);
      if (s.status === "done") { pendingTurnRef.current = null; applyCoachResult(s); }
      else pushRecoveryNote(rec.turnId, s.status === "not_run" ? "not_run" : "processing_again");
    } finally {
      inflightRef.current = Math.max(0, inflightRef.current - 1);
      if (inflightRef.current === 0) { setCoachWait(""); setCoachBusy(false); }
    }
  }, [runCoach, recoverTurn, applyCoachResult, pushRecoveryNote]);

  // REPAIR §2: provisional-thesis judgment (student decides; TWS never installs by fiat). YES installs the
  // student's EXACT wording; the other choices route the coach to scaffold revision / the structural judgment.
  // Defined ABOVE sendChat so a free-text "yes"/"not sure" while a proposal is on the table can route here too.
  const acceptProvisionalThesis = useCallback(async () => {
    if (!thesisProposal) return;
    const cand = thesisProposal.text;
    setThesisProposal(null);
    try { const r = await wsThesis(sessionId, cand); if (r?.ot) applyOt(r.ot, r.structure); if (r?.document) setDoc(r.document); } catch (e) { /* ignore */ }
    runCoach("", "the student accepted this as their provisional thesis (their own words)");
  }, [thesisProposal, sessionId, applyOt, runCoach]);
  // A rejection RETURNS THE JUDGMENT TO THE STUDENT: the candidate ceases to be a proposal (the server records the verdict and never puts
  // the same reading again), and the student is taken straight to the conversation to say / revise their main point in their own words.
  const toOwnWords = () => { try { chatRef.current && chatRef.current.focus(); } catch (e) { /* ignore */ } };
  const reviseProvisionalThesis = useCallback(() => { setThesisProposal(null); toOwnWords(); runCoach("", "the student said the provisional thesis is not quite right and wants to revise it"); }, [runCoach]);
  const unsureProvisionalThesis = useCallback(() => { setThesisProposal(null); toOwnWords(); runCoach("", "the student is not sure whether this should be their provisional thesis"); }, [runCoach]);

  // FO-unification: the student accepts (or declines) shaping their thinking as an essay. Accept turns on the
  // functional regions IN THE SAME MAP; decline dismisses the offer without nagging. Either way the coach
  // continues in the same living workspace — no new screen.
  const activateRegions = useCallback(async () => {
    setRegionOffer(null);
    try { const r = await wsActivateRegions(sessionId); if (r?.ot) applyOt(r.ot, r.structure); } catch (e) { /* ignore */ }
    runCoach("", "the student chose to start shaping their thinking as an essay — the map now has an introduction, a body, and a conclusion");
  }, [sessionId, applyOt, runCoach]);
  const declineRegions = useCallback(async () => {
    setRegionOffer(null);
    try { const r = await wsDeclineRegions(sessionId); if (r?.ot) applyOt(r.ot); } catch (e) { /* ignore */ }
    runCoach("", "the student is not ready to shape their thinking as an essay yet and wants to keep developing");
  }, [sessionId, applyOt, runCoach]);
  const placeThesisRegion = useCallback(async (region) => {
    setThesisRegion((region || "").toLowerCase());
    try { const r = await wsThesisRegion(sessionId, region); if (r?.ot) applyOt(r.ot); } catch (e) { /* ignore */ }
  }, [sessionId, applyOt]);

  // EARLY EXTERNALIZATION: clearing is a plain UNDO of the shape TWS externalized — the student's own ideas
  // return to loose, exactly as they were. It is NOT an authorship decision and carries no coach turn: the
  // student simply chooses to organize their own way. (Accepting is implicit — the student just works with it.)
  const clearLatentStructure = useCallback(async () => {
    setLatentStructure(null);
    try { const r = await wsClearLatent(sessionId); if (r?.ot) applyOt(r.ot); } catch (e) { /* ignore */ }
  }, [sessionId, applyOt]);

  const sendChat = useCallback(async () => {
    const m = chatInput.trim();
    if (!m || coachBusy) return;
    // ROBUST FREE-TEXT JUDGMENT (authentic-test repair): while a provisional-thesis proposal is on the table,
    // a short unambiguous "yes" / "not sure" / "no" typed in chat must be treated as the SAME judgment as the
    // buttons (never lost as an uninterpreted message). Substantive text falls through — the student is revising.
    if (thesisProposal) {
      const norm = m.toLowerCase().replace(/[^a-z ]/g, " ").replace(/\s+/g, " ").trim();
      const short = norm.split(" ").filter(Boolean).length <= 5;
      const AFFIRM = ["yes", "yeah", "yep", "yup", "sure", "ok", "okay", "use it", "use this", "use this one",
        "yes please", "yes use it", "that works", "sounds good", "definitely", "that s it", "correct", "right",
        "yes that s it", "keep it", "go with it", "yes lets use it", "yes let s use it"];
      const UNSURE = ["not sure", "im not sure", "i m not sure", "unsure", "idk", "i dont know", "i don t know",
        "dont know", "not really sure", "maybe"];
      const DECLINE = ["no", "nope", "not quite", "not really", "change it", "i want to change it", "not exactly"];
      if (short && (AFFIRM.includes(norm) || UNSURE.includes(norm) || DECLINE.includes(norm))) {
        setThread((t) => [...t, { id: `stu-${Date.now()}`, role: "student", text: m, focus: [] }]);
        setChatInput("");
        if (AFFIRM.includes(norm)) acceptProvisionalThesis();
        else if (UNSURE.includes(norm)) unsureProvisionalThesis();
        else reviseProvisionalThesis();
        return;
      }
    }
    // MANUAL-ACTION CLOSURE (repair #1): a typed "done" while TWS waits on a map change is the same signal as the
    // "I've done this" control — TWS re-inspects the actual map; the click/word is never treated as proof.
    if (awaiting && isDoneSignal(m)) {
      setThread((t) => [...t, { id: `stu-${Date.now()}`, role: "student", text: m, focus: [] }]);
      setChatInput("");
      selfDoneRef.current = true; setAwaiting(null);
      await runCoach("", "operation_done");
      return;
    }
    // ORIENTATION — a bare, unelaborated "I don't know" / "I'm confused" / "what do you mean?" is strong evidence the
    // current scaffold failed: ask what's getting in the way (the typed text stays in the box; nothing is sent yet).
    const dc = difficultyCheck(m, diffMemRef.current); diffMemRef.current = dc.memory;
    if (dc.trigger) { setDifficulty({ kind: dc.trigger, at: Date.now() }); return; }
    setThread((t) => [...t, { id: `stu-${Date.now()}`, role: "student", text: m, focus: [] }]);
    setChatInput("");
    // an identical resend of an unresolved turn reuses its turn_id — the server replays a completed turn, never re-runs it
    const reuse = (pendingTurnRef.current && pendingTurnRef.current.message === m) ? { turn_id: pendingTurnRef.current.turnId } : {};
    const ok = await runCoach(m, "", feiPhase !== "done" ? feiPhase : "", reuse);
    if (ok === false) setChatInput(m);   // REPAIR 10 — restore the typed text so a technical failure never loses it
  }, [chatInput, coachBusy, runCoach, feiPhase, thesisProposal, acceptProvisionalThesis, reviseProvisionalThesis, unsureProvisionalThesis, awaiting]);

  // ORIENTATION — hand a stated difficulty / help request to the EXISTING coach as an ordinary student turn.
  const sendToCoach = useCallback(async (text) => {
    if (!text || coachBusy) return;
    setThread((t) => [...t, { id: `stu-${Date.now()}`, role: "student", text, focus: [] }]);
    await runCoach(text, "", feiPhase !== "done" ? feiPhase : "");
  }, [coachBusy, runCoach, feiPhase]);
  const difficultyDone = useCallback((result) => { setDifficulty(null); if (result === "sent") setChatInput(""); }, []);
  useEffect(() => { if (awaiting) lastAwaitingRef.current = awaiting; awaitingRef.current = awaiting; }, [awaiting]);
  // MANUAL-ACTION CLOSURE (repair #1): when TWS is waiting on a map change and the student performs a structural change
  // by hand (drag / move out / ungroup / reorder / edit) instead of tapping the control, the OBSERVED change resumes the
  // dialogue automatically — TWS re-inspects the actual map. DONE is needed only when nothing observable happened.
  const resumeIfAwaiting = useCallback(() => {
    if (!awaitingRef.current) return false;
    selfDoneRef.current = true;
    awaitingRef.current = null;
    setAwaiting(null);
    runCoach("", "operation_done");
    return true;
  }, [runCoach]);

  // ---- questions (problem structure) — added within the current FEI phase ----
  const addQuestion = useCallback(async (fei) => {
    const text = (qDraft[fei] || "").trim();
    if (!text || addingQ) return;
    setAddingQ(true);
    try {
      const r = await wsAddQuestion(sessionId, { text, source: "student", fei });
      applyOt(r.ot);
      setQDraft((d) => ({ ...d, [fei]: "" }));
      setAddOpen(null);
      // Dialogue RECOGNIZES the directly-added question (item 7) with a local acknowledgment. We do NOT
      // auto-fire the coach here: a coach turn re-saves the whole OT and, when a second Add lands while
      // that turn is in flight, its stale snapshot clobbered the new question (the reported multi-Add
      // failure). The coach still engages on the student's own dialogue and when they advance.
      setThread((t) => [...t, { id: `ack-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`, role: "tws", focus: [], focusQ: [],
        text: `Added to "${FEI[fei]?.label || "your questions"}": "${text}". Add another if there's more here, or move on when you're ready.` }]);
    } catch (e) { toast("Couldn't add that just now — please try again."); } finally { setAddingQ(false); }
  }, [qDraft, addingQ, sessionId, applyOt]);

  // The student advances the representation themselves — including concluding a hidden category is
  // not needed ("I don't think I need one"). Absence is a legitimate intellectual conclusion.
  const advancePhase = useCallback((to) => {
    setAddOpen(null);
    setFeiPhase(to || "done"); // all teaching happened up front; activity moves directly between the 3 kinds
  }, []);

  const saveQEdit = useCallback(async () => {
    if (!editingQ) return;
    const r = await wsPatchQuestion(sessionId, editingQ.id, { text: editingQ.text });
    applyOt(r.ot); setEditingQ(null);
  }, [editingQ, sessionId, applyOt]);

  const toggleQResolved = useCallback(async (q) => {
    const r = await wsPatchQuestion(sessionId, q.id, { status: q.status === "resolved" ? "open" : "resolved" });
    applyOt(r.ot);
  }, [sessionId, applyOt]);

  const deleteQuestion = useCallback(async (qid) => {
    const r = await wsDeleteQuestion(sessionId, qid); applyOt(r.ot);
  }, [sessionId, applyOt]);

  const commitQRel = useCallback(async (toId) => {
    if (!qConnecting || qConnecting === toId) { setQConnecting(null); return; }
    const r = await wsAddQuestionRel(sessionId, { from_id: qConnecting, to_id: toId, relation: "needed for" });
    applyOt(r.ot);
    const from = questions.find((q) => q.id === qConnecting);
    const to = questions.find((q) => q.id === toId);
    setQConnecting(null);
    runCoach("", `showed that one question is needed to answer another: "${from?.text || ""}" is needed for "${to?.text || ""}".`);
  }, [qConnecting, sessionId, applyOt, questions, runCoach]);

  const deleteQRel = useCallback(async (rid) => {
    const r = await wsDeleteQuestionRel(sessionId, rid); applyOt(r.ot);
  }, [sessionId, applyOt]);

  // ---- ideas ----
  const addIdea = useCallback(async () => {
    const text = newIdea.trim();
    if (!text || adding) return;
    setAdding(true);
    try {
      const r = await wsAddItem(sessionId, { text, source: "student", status: newIsQuestion ? "need_to_figure_out" : "idea" });
      applyOt(r.ot); setNewIdea(""); setNewIsQuestion(false);
      const ws = r.ot.workspace || { items: [], relationships: [] };
      const nS = (ws.items || []).filter((i) => i.source === "student").length;
      const nR = (ws.relationships || []).length;
      if (nS >= 2 && !coachBusy) runCoach("", `added a new idea: "${text}". They now have ${nS} ideas and ${nR} relationship(s) among them.`);
    } catch (e) { /* keep */ } finally { setAdding(false); }
  }, [newIdea, newIsQuestion, adding, sessionId, applyOt, coachBusy, runCoach]);

  const saveEdit = useCallback(async () => {
    if (!editing) return;
    const r = await wsPatchItem(sessionId, editing.id, { text: editing.text });
    applyOt(r.ot); setEditing(null);
    resumeIfAwaiting();
  }, [editing, sessionId, applyOt, resumeIfAwaiting]);

  const toggleUncertain = useCallback(async (it) => {
    const r = await wsPatchItem(sessionId, it.id, { status: it.status === "need_to_figure_out" ? "idea" : "need_to_figure_out" });
    applyOt(r.ot);
  }, [sessionId, applyOt]);

  const deleteItem = useCallback(async (id) => { const r = await wsDeleteItem(sessionId, id); applyOt(r.ot); }, [sessionId, applyOt]);

  // UNIFIED WORKSPACE — the student decides TWS-provisional structure with one tap: KEEP adopts it (a patch on the
  // same placement claims it as theirs); NOT THIS removes a provisional sub-part (its words stay in the parent unit)
  // or takes a provisionally placed unit back to Still Developing (the location is remembered as rejected).
  const adoptProvisional = useCallback(async (id) => {
    const it = items.find((i) => i.id === id);
    if (!it) return;
    const r = await wsPatchItem(sessionId, id, it.parent_id ? { parent_id: it.parent_id } : { under_thesis: !!it.under_thesis });
    applyOt(r.ot);
  }, [sessionId, items, applyOt]);
  const rejectProvisional = useCallback(async (id, status) => {
    const it = items.find((i) => i.id === id);
    if (!it) return;
    const r = status === "provisional_unit" ? await wsDeleteItem(sessionId, id) : await wsPatchItem(sessionId, id, { parent_id: "" });
    applyOt(r.ot);
  }, [sessionId, items, applyOt]);
  // UDWW 3C-1 — TWS-provisional STRUCTURE (never content): dismiss a functional read; keep / not-this a suggested relation.
  // REJECT STRUCTURE ≠ REJECT CONTENT: none of these touches a unit's words, place or history.
  const dismissRole = useCallback(async (id) => {
    const r = await wsPatchItem(sessionId, id, { provisional_role: "" });
    applyOt(r.ot);
  }, [sessionId, applyOt]);
  const adoptProvisionalRel = useCallback(async (relId) => {
    const rel = rels.find((x) => x.id === relId);
    if (!rel) return;
    const r = await wsPatchRel(sessionId, relId, { from_id: rel.from_id, to_id: rel.to_id, relation_text: rel.relation_text || "", confirmed_by_student: true });
    applyOt(r.ot);
  }, [sessionId, rels, applyOt]);
  const rejectProvisionalRel = useCallback(async (relId) => {
    const r = await wsDeleteRel(sessionId, relId);
    applyOt(r.ot);
  }, [sessionId, applyOt]);

  const toggleIdeaQuestion = useCallback(async (ideaId, qid) => {
    const it = items.find((i) => i.id === ideaId);
    const cur = it?.question_ids || [];
    const next = cur.includes(qid) ? cur.filter((x) => x !== qid) : [...cur, qid];
    const r = await wsPatchItem(sessionId, ideaId, { question_ids: next });
    applyOt(r.ot);
  }, [items, sessionId, applyOt]);

  const commitSeparate = useCallback(async () => {
    if (!separating) return;
    const parts = separating.parts.map((p) => p.trim()).filter(Boolean);
    if (parts.length < 2) return;
    const src = items.find((i) => i.id === separating.id);
    const r = await wsSeparateItem(sessionId, separating.id, parts);
    applyOt(r.ot); setSeparating(null);
    runCoach("", `pulled one idea apart into: ${parts.map((p) => `"${p}"`).join(", ")} (was: "${src?.text || ""}")`);
  }, [separating, items, sessionId, applyOt, runCoach]);

  const commitRelationship = useCallback(async () => {
    if (!relDraft) return;
    const from = items.find((i) => i.id === relDraft.from_id);
    const to = items.find((i) => i.id === relDraft.to_id);
    const r = await wsAddRel(sessionId, { from_id: relDraft.from_id, to_id: relDraft.to_id, relation_text: relDraft.text.trim() });
    applyOt(r.ot); setRelDraft(null); setConnecting(null);
    runCoach("", `connected two ideas: "${from?.text || ""}" ${relDraft.text.trim() ? `(${relDraft.text.trim()}) ` : ""}--> "${to?.text || ""}"`);
  }, [relDraft, items, sessionId, applyOt, runCoach]);

  const commitGroup = useCallback(async (parentId) => {
    if (!grouping) return;
    const child = items.find((i) => i.id === grouping);
    const parent = items.find((i) => i.id === parentId);
    const r = await wsPatchItem(sessionId, grouping, { parent_id: parentId });
    applyOt(r.ot); setGrouping(null);
    runCoach("", `made "${child?.text || ""}" part of the larger idea "${parent?.text || ""}"`);
  }, [grouping, items, sessionId, applyOt, runCoach]);

  const unGroup = useCallback(async (id) => { const r = await wsPatchItem(sessionId, id, { parent_id: "" }); applyOt(r.ot); resumeIfAwaiting(); }, [sessionId, applyOt, resumeIfAwaiting]);

  // The student approves a pending captured idea (§17) — it becomes a durable student-authored item.
  const keepCapture = useCallback(async (idx) => {
    const c = pendingCaptures[idx];
    if (!c) return;
    const r = await wsAddItem(sessionId, { text: c.text, source: "student", status: "idea" });
    let nextOt = r.ot;
    const created = r.item;
    if (created && (c.question_ids || []).length) {
      const r2 = await wsPatchItem(sessionId, created.id, { question_ids: c.question_ids });
      nextOt = r2.ot;
    }
    applyOt(nextOt);
    setPendingCaptures((prev) => prev.filter((_, i) => i !== idx));
  }, [pendingCaptures, sessionId, applyOt]);

  const dismissCapture = useCallback((idx) => {
    setPendingCaptures((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // TWS asked how two ideas relate; the STUDENT articulates it (§5/§20) — opens the relation editor.
  // v1.3.2 §8/§16: a question identified during problem representation is now a place to think —
  // selecting it asks the SUBSTANTIVE version (reuses the existing coach; no new call) so the
  // student's answer is captured as an idea linked to that same question object.
  const workOnQuestion = useCallback((q) => {
    setFocusQIds([q.id]);
    setSelectedIdeaId(null);
    runCoach("", `The student wants to work on this question now: "${q.text}". You are now PAST problem representation — ask them the substantive version of this question so they can begin answering it, and capture their answer as an idea linked to this question (question_id ${q.id}).`, "");
  }, [runCoach]);

  // ---- v1.3.4 unified structural operations (drag, click-move, and fast-follow verbal all call these) ----
  const [lastMove, setLastMove] = useState(null);   // single-step undo of the last structural move
  const primaryQof = (it) => (it.question_ids || []).find((qid) => questions.some((q) => q.id === qid));
  const ideaDescendantsList = (id) => { const out = []; const walk = (x) => { items.filter((i) => i.parent_id === x).forEach((c) => { out.push(c.id); walk(c.id); }); }; walk(id); return out; };
  const questionDescendantsList = (id) => { const out = []; const walk = (x) => { qrels.filter((r) => r.to_id === x).forEach((r) => { if (!out.includes(r.from_id)) { out.push(r.from_id); walk(r.from_id); } }); }; walk(id); return out; };

  const captureParent = (node) => {
    if (node.type === "question") {
      const q = questions.find((x) => x.id === node.id);
      if (q?.parent_item_id) return { type: "idea", id: q.parent_item_id };
      if (q?.unplaced) return { type: "loose" };
      const qr = qrels.find((r) => r.from_id === node.id);
      return qr ? { type: "question", id: qr.to_id } : { type: "root" };
    }
    const it = items.find((i) => i.id === node.id);
    if (!it) return { type: "root" };
    if (it.parent_id) return { type: "idea", id: it.parent_id };
    const pq = primaryQof(it);
    return pq ? { type: "question", id: pq } : { type: "loose" };
  };
  const removeQuestionParents = async (qid) => { let ot = null; for (const qr of qrels.filter((r) => r.from_id === qid)) { const r = await wsDeleteQuestionRel(sessionId, qr.id); ot = r.ot; } return ot; };
  const applyReparent = async (node, target) => {
    if (node.type === "question") {
      await removeQuestionParents(node.id);
      if (target.type === "question") { await wsAddQuestionRel(sessionId, { from_id: node.id, to_id: target.id, relation: "part of" }); const r = await wsPatchQuestion(sessionId, node.id, { parent_item_id: "", unplaced: false }); return r.ot; }
      if (target.type === "idea") { const r = await wsPatchQuestion(sessionId, node.id, { parent_item_id: target.id, unplaced: false }); return r.ot; }
      const r = await wsPatchQuestion(sessionId, node.id, { parent_item_id: "", unplaced: target.type === "loose" }); return r.ot; // root or not-placed
    }
    let patch;
    if (target.type === "question") patch = { question_ids: [target.id], parent_id: "", under_thesis: false };
    else if (target.type === "idea") patch = { parent_id: target.id, under_thesis: false };  // keep question_ids as provenance
    else if (target.type === "root") patch = { parent_id: "", question_ids: [], under_thesis: true };   // a major part OF the main idea
    else patch = { parent_id: "", question_ids: [], under_thesis: false };                               // keep separate / not placed
    const r = await wsPatchItem(sessionId, node.id, patch); return r.ot;
  };
  // THE single reparent+position operation used by drag-and-drop AND click "Move under…"
  // Parent relationship and sibling ORDER are independent (v1.3.5): after reparenting, if a sibling
  // slot was requested (drop before a sibling, or the end drop-line "__END__"), we recompute the merged
  // child order under the target (questions AND ideas together) and persist explicit `position`s.
  const cmpSibNode = (a, b) => {
    const pa = typeof a.position === "number" ? a.position : null;
    const pb = typeof b.position === "number" ? b.position : null;
    if (pa !== null && pb !== null) return pa - pb;
    if (pa !== null) return -1;
    if (pb !== null) return 1;
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  };
  const siblingsUnder = (otDoc, target) => {
    const qs = (otDoc.problem_structure?.questions || []).filter((q) => q.source === "student");
    const its = otDoc.workspace?.items || [];
    const qr = otDoc.problem_structure?.question_relationships || [];
    const qbi = (id) => qs.find((q) => q.id === id);
    const pq = (it) => (it.question_ids || []).find((qid) => qbi(qid));
    let subs = [], ideas = [];
    if (target.type === "question") {
      subs = qr.filter((r) => r.to_id === target.id).map((r) => qbi(r.from_id)).filter(Boolean).map((n) => ({ type: "question", node: n }));
      ideas = its.filter((i) => !i.parent_id && pq(i) === target.id).map((n) => ({ type: "idea", node: n }));
    } else if (target.type === "idea") {
      ideas = its.filter((i) => i.parent_id === target.id).map((n) => ({ type: "idea", node: n }));
      subs = qs.filter((q) => q.parent_item_id === target.id).map((n) => ({ type: "question", node: n }));
    } else { // root
      const isChildQ = (id) => qr.some((r) => r.from_id === id && qbi(r.to_id));
      subs = qs.filter((q) => !isChildQ(q.id) && !q.parent_item_id && !q.unplaced).map((n) => ({ type: "question", node: n }));
      ideas = its.filter((i) => !i.parent_id && !pq(i) && its.some((c) => c.parent_id === i.id)).map((n) => ({ type: "idea", node: n }));
    }
    return [...subs, ...ideas].sort((a, b) => cmpSibNode(a.node, b.node));
  };
  // Reorder-only (Move Up/Down and drag-between-siblings): persist the full sibling order; parent unchanged.
  const reorderSiblings = async (order) => {
    const r = await wsReorderSiblings(sessionId, order);
    if (r.ot) applyOt(r.ot);
    resumeIfAwaiting();
  };
  const applyStructuralMove = async (node, target, beforeId, record = true) => {
    // FO-unification: placing a part INTO an essay region is a top-level placement on the SAME item (region
    // field), never a reparent. The student decides where each of their parts belongs for a reader.
    // GLOBAL STRUCTURE OWNERSHIP: a region move is the student's structural operation — no coaching turn is fired by it (the
    // `place_region` event is recorded; the next genuine TWS turn reads the changed arrangement); it is undoable as ONE step.
    if (target && target.type === "region") {
      const reg = target.region || "";
      const src = node.type === "question" ? questions.find((x) => x.id === node.id) : items.find((i) => i.id === node.id);
      const prev = node.type === "idea" && src ? [regionSnapshot(src)] : null;
      const r = node.type === "question"
        ? await wsPatchQuestion(sessionId, node.id, { region: reg, unplaced: false, parent_item_id: "" })
        : await wsPatchItem(sessionId, node.id, { region: reg, parent_id: "", question_ids: [], under_thesis: true });
      if (r?.ot) applyOt(r.ot);
      if (record && prev) setLastMove({ kind: "regions", prev });
      return;
    }
    const prev = record ? captureParent(node) : null;
    let ot = await applyReparent(node, target);
    const wantsOrder = !!beforeId && (target.type === "question" || target.type === "idea" || target.type === "root");
    if (ot && wantsOrder) {
      const sibs = siblingsUnder(ot, target).filter((s) => s.node.id !== node.id);
      const movedRef = { type: node.type, id: node.id };
      const order = [];
      if (beforeId !== "__END__") {
        const idx = sibs.findIndex((s) => s.node.id === beforeId);
        sibs.forEach((s, k) => { if (k === idx) order.push(movedRef); order.push({ type: s.type, id: s.node.id }); });
        if (idx < 0) order.push(movedRef);
      } else {
        sibs.forEach((s) => order.push({ type: s.type, id: s.node.id }));
        order.push(movedRef);
      }
      const r = await wsReorderSiblings(sessionId, order); ot = r.ot;
    }
    if (ot) applyOt(ot);
    if (record) setLastMove({ node, prevTarget: prev });
    if (record) resumeIfAwaiting();
  };
  const pullOut = async (node) => {
    const prev = captureParent(node); let ot = null;
    if (node.type === "question") {
      const q = questions.find((x) => x.id === node.id);
      if (q?.parent_item_id) {
        const pIdea = items.find((i) => i.id === q.parent_item_id);
        if (pIdea?.parent_id) { const r = await wsPatchQuestion(sessionId, node.id, { parent_item_id: pIdea.parent_id, unplaced: false }); ot = r.ot; }
        else {
          const pq = pIdea ? primaryQof(pIdea) : null;
          if (pq) { await wsPatchQuestion(sessionId, node.id, { parent_item_id: "" }); const r = await wsAddQuestionRel(sessionId, { from_id: node.id, to_id: pq, relation: "part of" }); ot = r.ot; }
          else { const r = await wsPatchQuestion(sessionId, node.id, { parent_item_id: "", unplaced: false }); ot = r.ot; }
        }
      } else {
        const qr = qrels.find((r) => r.from_id === node.id);
        if (!qr) return;
        const grand = qrels.find((r) => r.from_id === qr.to_id);
        await removeQuestionParents(node.id);
        if (grand) { const r = await wsAddQuestionRel(sessionId, { from_id: node.id, to_id: grand.to_id, relation: "part of" }); ot = r.ot; }
        else { const r = await wsPatchQuestion(sessionId, node.id, { parent_item_id: "", unplaced: false }); ot = r.ot; }
      }
    } else {
      const it = items.find((i) => i.id === node.id); if (!it) return;
      if (it.parent_id) {
        const parent = items.find((i) => i.id === it.parent_id);
        if (parent?.parent_id) { const r = await wsPatchItem(sessionId, node.id, { parent_id: parent.parent_id }); ot = r.ot; }
        else { const pq = parent ? primaryQof(parent) : null; const r = await wsPatchItem(sessionId, node.id, pq ? { parent_id: "", question_ids: [pq] } : { parent_id: "", question_ids: [], under_thesis: false }); ot = r.ot; }
      } else { const r = await wsPatchItem(sessionId, node.id, { parent_id: "", question_ids: [], under_thesis: false }); ot = r.ot; }
    }
    if (ot) applyOt(ot);
    setLastMove({ node, prevTarget: prev });
    resumeIfAwaiting();
  };
  const undoMove = async () => {
    if (!lastMove) return;
    if (lastMove.kind === "regions") {   // ONE step restores the complete prior region configuration (a move or a boundary assertion)
      const { prev } = lastMove; setLastMove(null);
      const r = await wsRegionsRestore(sessionId, prev); if (r?.ot) applyOt(r.ot);
      return;
    }
    const { node, prevTarget } = lastMove; setLastMove(null); await applyStructuralMove(node, prevTarget, null, false);
  };
  // "INTRODUCTION / BODY / CONCLUSION STARTS HERE" from Essay view — ONE assertion on the same canonical `item.region` state (one save);
  // the backend derives the spans from document order and returns the prior configuration for one-step undo. Never a coaching turn.
  const regionSnapshot = (it) => ({ id: it.id, region: (it.region || "").toLowerCase(), under_thesis: !!it.under_thesis, placement_provenance: it.placement_provenance || null, parent_id: it.parent_id || null });
  const setRegionStart = async (itemId, region) => {
    const r = await wsRegionStart(sessionId, itemId, region);
    if (r?.ot) applyOt(r.ot);
    if (r?.prev && r.prev.length) setLastMove({ kind: "regions", prev: r.prev });
  };

  const deleteNode = async (node, mode) => {
    if (node.type === "idea") {
      if (mode === "branch") {
        const ids = [...ideaDescendantsList(node.id), node.id]; let ot = null;
        for (const id of ids) { const r = await wsDeleteItem(sessionId, id); ot = r.ot; }
        if (ot) applyOt(ot);
      } else {
        const it = items.find((i) => i.id === node.id);
        for (const k of items.filter((i) => i.parent_id === node.id)) {
          if (it?.parent_id) await wsPatchItem(sessionId, k.id, { parent_id: it.parent_id });
          else { const pq = it ? primaryQof(it) : null; await wsPatchItem(sessionId, k.id, pq ? { parent_id: "", question_ids: [pq] } : { parent_id: "", question_ids: [] }); }
        }
        const r = await wsDeleteItem(sessionId, node.id); applyOt(r.ot);
      }
    } else {
      const qr = qrels.find((r) => r.from_id === node.id);
      const parentQ = qr ? qr.to_id : null;
      if (mode === "branch") {
        const subqs = [...questionDescendantsList(node.id), node.id];
        const kill = new Set();
        items.filter((i) => !i.parent_id && subqs.includes(primaryQof(i))).forEach((a) => { kill.add(a.id); ideaDescendantsList(a.id).forEach((d) => kill.add(d)); });
        let ot = null;
        for (const id of kill) { const r = await wsDeleteItem(sessionId, id); ot = r.ot; }
        for (const qid of subqs) { const r = await wsDeleteQuestion(sessionId, qid); ot = r.ot; }
        if (ot) applyOt(ot);
      } else {
        for (const sq of qrels.filter((r) => r.to_id === node.id).map((r) => r.from_id)) { await removeQuestionParents(sq); if (parentQ) await wsAddQuestionRel(sessionId, { from_id: sq, to_id: parentQ, relation: "part of" }); }
        for (const a of items.filter((i) => !i.parent_id && primaryQof(i) === node.id)) { await wsPatchItem(sessionId, a.id, { question_ids: parentQ ? [parentQ] : [] }); }
        const r = await wsDeleteQuestion(sessionId, node.id); applyOt(r.ot);
      }
    }
  };

  const onCreateQuestion = async (target, text) => {
    const t = (text || "").trim(); if (!t) return;
    const r = await wsAddQuestion(sessionId, { text: t, source: "student", fei: null });
    const id = r.question.id;
    if (target.type === "root") { applyOt(r.ot); return; }
    if (target.type === "loose") { const r2 = await wsPatchQuestion(sessionId, id, { unplaced: true }); applyOt(r2.ot); return; }
    await applyStructuralMove({ type: "question", id }, target, null, false);
  };
  const onCreateIdea = async (target, text) => {
    const t = (text || "").trim(); if (!t) return;
    const r = await wsAddItem(sessionId, { text: t, source: "student", status: "idea" });
    const id = r.item.id;
    if (target.type === "loose") { applyOt(r.ot); return; }
    await applyStructuralMove({ type: "idea", id }, target, null, false);
  };

  // ---- map operations reused as-is (develop, save, connect, separate, add loose) ----
  const mmDevelop = useCallback((node) => {
    setFocusIds(node.type === "idea" ? [node.id] : []);
    // PART⇄WHOLE: the student's answer DEVELOPS this card (recorded on it as its development, consolidated
    // upward when it materially changes it) — it is not automatically another loose card.
    runCoach("", `The student wants to develop this idea further: "${node.text}". Ask ONE concise question that pushes their thinking about it deeper — toward a subordinate idea, a consequence, an example, or a qualification. Do NOT supply the content. Their answer develops THIS idea (development.function elaborates/revises/differentiates with this card's id), not a new loose card, unless it is genuinely a new idea.`, "",
      node.type === "idea" ? { develop_item_id: node.id } : {});
  }, [runCoach]);
  const mmSaveIdea = useCallback(async (id, text, label, formatting) => {
    const patch = { text };
    if (formatting !== undefined) patch.formatting = formatting;          // presentational metadata only — saved beside the plain text
    if (label !== undefined) { patch.label = label; patch.label_authored = true; }
    const r = await wsPatchItem(sessionId, id, patch); applyOt(r.ot);
  }, [sessionId, applyOt]);
  // UDWW Pass 3B — COMPOSE LOCALLY. Open / return are structural acts on the registry only (no engine, no LLM); a
  // turn writes the unit FIRST and then polls for the coaching that fills the pending dialogue entry.
  const refreshCompose = useCallback(async (uid) => {
    const v = await wsComposeGet(sessionId, uid);
    setComposeView(v);
    if (v.ot) applyOt(v.ot);
    (v.dialogue || []).forEach((d) => {
      if (d.role === "tws" && d.turn_id && d.text && !d.pending && !composeSeenRef.current.has(d.turn_id)) {
        composeSeenRef.current.add(d.turn_id);
        setThread((t) => [...t, { id: d.turn_id, role: "tws", text: d.text, focus: [uid], focusQ: [], compose: true }]);
      }
    });
    return v;
  }, [sessionId, applyOt]);
  useEffect(() => {
    if (!composeFocus) { setComposeView(null); return; }
    refreshCompose(composeFocus).catch(() => {});
  }, [composeFocus, refreshCompose, composeKick]);
  const composePending = !!(composeView && composeView.pending);
  useEffect(() => {
    if (!composeFocus || !composePending) return;
    const t = setInterval(() => { refreshCompose(composeFocus).catch(() => {}); }, 2500);
    return () => clearInterval(t);
  }, [composeFocus, composePending, refreshCompose]);
  const composeOpen = useCallback(async (uid) => {
    try { const r = await wsComposeOpen(sessionId, uid); applyOt(r.ot); setComposeFocus(intentionalEntry((r.ot?.compose || {})[uid]) ? uid : null); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't open this part for writing."); }
  }, [sessionId, applyOt]);
  const composeReturn = useCallback(async (uid) => {
    const r = await wsComposeReturn(sessionId, uid); applyOt(r.ot); setComposeFocus(null);
  }, [sessionId, applyOt]);
  const composeTurn = useCallback(async (uid, text, kind, message, formatting) => {
    setComposeBusy(true);
    try {
      const r = await wsComposeTurn(sessionId, uid, { text, kind, message: message || "", ...(formatting !== undefined ? { formatting } : {}) });   // formatting: presentation beside the words
      applyOt(r.ot);
      setComposeView((v) => (v ? { ...v, pending: true, unit: { ...(v.unit || {}), text: r.unit_text } } : v));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send this part to The Writing Studio.");
    } finally { setComposeBusy(false); }
  }, [sessionId, applyOt]);
  const mmSaveQuestion = useCallback(async (id, text) => { const r = await wsPatchQuestion(sessionId, id, { text }); applyOt(r.ot); }, [sessionId, applyOt]);
  const mmConnect = useCallback(async (fromId, toId, text) => {
    const from = items.find((i) => i.id === fromId); const to = items.find((i) => i.id === toId);
    const r = await wsAddRel(sessionId, { from_id: fromId, to_id: toId, relation_text: text, source: "student", confirmed_by_student: true });
    applyOt(r.ot);
    runCoach("", `connected two ideas: "${from?.text || ""}" ${text ? `(${text}) ` : ""}--> "${to?.text || ""}"`);
  }, [items, sessionId, applyOt, runCoach]);
  // LIVING UNIT BOUNDARIES (UDWW pass 2): SPLIT keeps the unit's identity and every word (the earlier whole is
  // history); JOIN makes one unit of several (verbatim, lineage kept). The coach is told in plain words.
  const mmSeparate = useCallback(async (id, parts) => {
    const src = items.find((i) => i.id === id);
    try {
      const r = await wsSeparateItem(sessionId, id, parts); applyOt(r.ot);
    } catch (e) {
      toast("Each part has to be in the unit's own words — copy the exact words from the unit."); return;
    }
    runCoach("", `split one unit into distinct parts: ${parts.map((p) => `"${p}"`).join(", ")} (the unit was: "${src?.text || ""}")`);
  }, [items, sessionId, applyOt, runCoach]);
  const mmJoin = useCallback(async (primaryId, otherId) => {
    const a = items.find((i) => i.id === primaryId); const b = items.find((i) => i.id === otherId);
    try {
      const r = await wsJoinItems(sessionId, [primaryId, otherId]); applyOt(r.ot);
    } catch (e) {
      toast("Those two can't be joined just now."); return;
    }
    runCoach("", `joined two units into one — said they are really the same point: "${a?.text || ""}" + "${b?.text || ""}"`);
  }, [items, sessionId, applyOt, runCoach]);
  // STRUCTURE VIEW AS ORDINARY WRITING: SPLIT a paragraph at the cursor / JOIN it with the next — the SAME canonical separate / join routes
  // (identity, words, history and lineage kept), with NO coach turn for the mechanical act: TWS reads the result at the next turn it is asked to.
  const mmSplitParagraph = useCallback(async (id, first, second) => {
    try { const r = await wsSeparateItem(sessionId, id, [first, second]); applyOt(r.ot); }
    catch (e) { toast(e?.response?.data?.detail ? "That paragraph couldn't be split there." : "That paragraph couldn't be split just now."); throw e; }
  }, [sessionId, applyOt]);
  const mmJoinNext = useCallback(async (id, nextId) => {
    try { const r = await wsJoinItems(sessionId, [id, nextId]); applyOt(r.ot); }
    catch { toast("Those two paragraphs can't be joined just now."); }
  }, [sessionId, applyOt]);
  // LIVING WORKSPACE (§16-19): promote a cluster to a section / name its function / revert. The coach is told
  // in plain terms so it can NAME what the student did (thought -> writing-for-a-reader), not announce mechanics.
  const mmMakeSection = useCallback(async (id, nodeKind, fn, notify = true) => {
    const src = items.find((i) => i.id === id);
    const r = await wsSetSection(sessionId, id, nodeKind, fn); applyOt(r.ot);
    if (notify) {
      const what = nodeKind === "idea"
        ? `turned the section back into an idea: "${src?.label || src?.text || ""}"`
        : `made "${src?.label || src?.text || ""}" a ${nodeKind}${fn ? ` whose job is ${fn}` : ""}`;
      runCoach("", what);
    }
    return src;
  }, [items, sessionId, applyOt, runCoach]);
  const mmAddLoose = useCallback(async (text) => {
    const t = (text || "").trim(); if (!t) return;
    const r = await wsAddItem(sessionId, { text: t, source: "student", status: "idea" }); applyOt(r.ot);
    const nS = (r.ot.workspace?.items || []).filter((i) => i.source === "student").length;
    if (nS >= 2 && !coachBusy) runCoach("", `added a new idea: "${t}". They now have ${nS} ideas.`);
  }, [sessionId, applyOt, coachBusy, runCoach]);

  const startRelate = useCallback(() => {
    if (!relatePrompt) return;
    setRelDraft({ from_id: relatePrompt.from_id, to_id: relatePrompt.to_id, text: "" });
    setRelatePrompt(null);
  }, [relatePrompt]);

  // v1.3 §6: the student expresses the connection RIGHT in the diagram panel — it becomes a visible edge.
  const submitRelate = useCallback(async () => {
    const text = relInput.trim();
    if (!relatePrompt || !text) return;
    const r = await wsAddRel(sessionId, {
      from_id: relatePrompt.from_id, to_id: relatePrompt.to_id,
      relation_text: text, source: "student", confirmed_by_student: true,
    });
    applyOt(r.ot);
    setRelInput("");
    setRelatePrompt(null);
  }, [relInput, relatePrompt, sessionId, applyOt]);
  // to thinking, with student-grounded provenance. TWS never authored the substance.
  const confirmDemo = useCallback(async () => {
    if (!demoRelation) return;
    const r = await wsAddRel(sessionId, {
      from_id: demoRelation.from_id, to_id: demoRelation.to_id,
      relation_text: demoRelation.relation_text || "connects to", source: "student",
      student_grounding: demoRelation.student_grounding || "",
      represented_by: "tws_from_student_expression", confirmed_by_student: true,
    });
    applyOt(r.ot);
    setDemoRelation(null);
  }, [demoRelation, sessionId, applyOt]);

  const changeDemo = useCallback(() => {
    if (!demoRelation) return;
    setRelDraft({ from_id: demoRelation.from_id, to_id: demoRelation.to_id, text: demoRelation.relation_text || "" });
    setDemoRelation(null);
  }, [demoRelation]);

  const saveRelEdit = useCallback(async () => {
    if (!editingRel) return;
    const rel = rels.find((x) => x.id === editingRel.id);
    const r = await wsPatchRel(sessionId, editingRel.id, { from_id: rel.from_id, to_id: rel.to_id, relation_text: editingRel.text.trim() });
    applyOt(r.ot); setEditingRel(null);
  }, [editingRel, rels, sessionId, applyOt]);

  const deleteRel = useCallback(async (id) => { const r = await wsDeleteRel(sessionId, id); applyOt(r.ot); }, [sessionId, applyOt]);

  // ---- thesis ----
  const saveThesis = useCallback(async (text) => {
    const r = await wsThesis(sessionId, text);
    applyOt(r.ot, r.structure);
    if (r.document) setDoc(r.document);
  }, [sessionId, applyOt]);
  // ESSAY-AS-WORKSPACE: "not my main idea" clears the judgment; the sentence stays in the paragraph. A new paragraph written in
  // the essay is ONE unit of the writing; TWS reads the changed writing on the coach turn that follows.
  const clearThesis = useCallback(() => saveThesis(""), [saveThesis]);
  const addParagraphQuiet = useCallback(async (text, formatting) => {
    const r = await wsAddItem(sessionId, { text, source: "student", status: "idea" });
    if (formatting && r && r.item && r.item.id) {                          // the new paragraph's formatting rides on the same item, same patch route
      const r2 = await wsPatchItem(sessionId, r.item.id, { formatting });
      applyOt(r2.ot);
    } else applyOt(r.ot);
    return r;
  }, [sessionId, applyOt]);
  const addParagraph = useCallback(async (text, formatting) => {
    await addParagraphQuiet(text, formatting);
    runCoach("", "the student wrote the next paragraph of their essay");
  }, [addParagraphQuiet, runCoach]);
  // SCRATCH PAD — thinking space beside the essay (Tools ▾ → Scratch pad, or its own bar under the essay). Its text reaches the essay ONLY through
  // the student's "Use in my essay": a new paragraph in their exact words (origin scratch_pad, student-authored); no coach call, no rewriting —
  // TWS reads the changed writing on the next turn like any paragraph the student wrote.
  const scratchKey = `compass_scratch_open|${sessionId}`;
  const [scratchOpen, setScratchOpen] = useState(() => { try { return sessionStorage.getItem(scratchKey) === "1"; } catch { return false; } });
  const toggleScratch = useCallback((force) => setScratchOpen((o) => { const n = typeof force === "boolean" ? force : !o; try { sessionStorage.setItem(scratchKey, n ? "1" : "0"); } catch { /* ignore */ } return n; }), [scratchKey]);
  const useScratchInEssay = useCallback(async (text) => {
    const r = await wsAddItem(sessionId, { text, source: "student", status: "idea", origin: "scratch_pad" });
    applyOt(r.ot);
  }, [sessionId, applyOt]);
  // ADD MY WORDS TO ESSAY — the student's OWN conversation words become a paragraph, exact and student-authored (server-side idempotent)
  const incorporateMessage = useCallback(async (m) => {
    try {
      const r = await wsIncorporate(sessionId, m.turn_id || "", m.text || "");
      if (r?.ot) applyOt(r.ot, r.structure);
      if (r?.document) setDoc(r.document);
      toast(r?.created ? "Your words are now in your essay, exactly as you wrote them." : "Those words are already in your essay.");
    } catch (e) { toast("Only words you wrote yourself can be added to the essay."); }
  }, [sessionId, applyOt]);
  // COMMIT BEFORE A TWS ACTION — the same save calls the editors use (patch / thesis / add + place), only for a draft that is non-empty AND
  // differs from its persisted text: no empty paragraphs, no no-op revisions, no manufactured writing-change evidence. Cleared first, so a
  // draft can never commit twice; restored if the save fails.
  const commitDrafts = useCallback(async () => {
    for (const d of unsavedDrafts(sessionId)) {
      clearDraft(sessionId, d.id);
      try {
        if (d.id === "thesis") { if (d.text !== (thesis || "").trim()) await saveThesis(d.text); }
        else if (d.id === "new") await addParagraphQuiet(d.text);
        else if (d.id.startsWith("new:")) {
          const r = await wsAddItem(sessionId, { text: d.text, source: "student", status: "idea" });
          const r2 = await wsPatchItem(sessionId, r.item.id, { region: d.id.slice(4), parent_id: "", question_ids: [], under_thesis: true });
          applyOt(r2.ot);
        } else {
          const it = items.find((i) => i.id === d.id);
          if (it && d.text !== unitEditText(it).trim() && d.text !== (it.text || "").trim()) await mmSaveIdea(d.id, d.text);
        }
      } catch (e) { setDraft(sessionId, d.id, d.text, d.opened); throw e; }
    }
  }, [sessionId, thesis, items, saveThesis, addParagraphQuiet, applyOt, mmSaveIdea]);
  commitDraftsRef.current = commitDrafts;

  // UDWW 3C-4b — the ESSAY VIEW reads the backend's projection of the SAME units: whenever the record's revision changes
  // (any map action, edit, move, coach turn) while the essay is open, the projection is re-read — never re-derived here.
  const otRev = ot?.rev;
  useEffect(() => {
    if (view !== "essay") return;
    let alive = true;
    wsGet(sessionId).then((r) => { if (alive && r && r.document) setDoc(r.document); }).catch(() => {});
    return () => { alive = false; };
  }, [view, otRev, sessionId]);

  // UDWW 3C-4b — A WORKING TITLE IS A DEVELOPMENTAL ACT (paragraph → apprehend → title → test → revise): TWS reads the
  // student's title against the paragraph it names. The title itself was already saved by the same label PATCH the map uses.
  const titleGiven = useCallback((unit, title) => {
    setFocusIds([unit.id]);
    runCoach("", `The student gave this paragraph a working title, in their own words: "${title}". A title is their representation of what the paragraph is DOING as a whole. Read TITLE ⇄ PARAGRAPH: does the paragraph do what the title says, and does the title capture what the paragraph does? If they fit, say so in one sentence and continue from what they are working on. If there is a gap, ask ONE concise question about it. Never rewrite the title or the paragraph; never supply a title of your own.`, "",
      { develop_item_id: unit.id });
  }, [runCoach]);

  // UDWW 3C-4b — MOVE ON: an obvious, quiet escape from the current developmental target. The student's words go through
  // the ordinary coach turn, so the backend's ONE deferral state (`_defer_signal` → `_defer_target`) handles it exactly as
  // a typed "let's move on" would: the target is set aside (not escalated, not re-asked) until they return to it.
  const liveTarget = ot?.dev_op && ot.dev_op.key && (ot.dev_op.operation || "continue") !== "continue" ? ot.dev_op : null;
  const moveOn = useCallback(() => sendToCoach("Let's move on for now."), [sendToCoach]);
  // GLOBAL COVERAGE IN ESSAY VIEW — the EXISTING checkpoint (essay.global_org.coverage, signature, stale) read over the canonical
  // regions; fetched (deterministic, no LLM) only while the writing frame shows the essay and structure is licensed, and only when the
  // architecture the student can see (regions / thesis) changes.
  const [coverage, setCoverage] = useState(null);
  const coverageKey = consolidated && structureLicensed && view === "essay"
    ? items.filter((i) => !i.parent_id).map((i) => `${i.id}:${i.region || ""}:${i.placement_provenance || ""}`).join("|") + `#${(ot?.thesis?.text || "").trim()}`
    : "";
  useEffect(() => {
    if (!coverageKey || !sessionId) { setCoverage(null); return; }
    let live = true;
    wsCoverageGet(sessionId).then((c) => { if (live) setCoverage(c); }).catch(() => { if (live) setCoverage(null); });
    return () => { live = false; };
  }, [coverageKey, sessionId]);
  const confirmCoverage = useCallback(async (choice, note = "") => {
    const c = await wsCoverageConfirm(sessionId, choice, note); setCoverage(c);
  }, [sessionId]);
  const deferredTargets = (ot?.target_ledger || []).filter((e) => e && e.kind === "deferred");
  // THE TRAJECTORY (consolidated loop): a projection of the controller's last decision + the support ledger for the learner — it decides nothing
  const progress = useMemo(() => (consolidated ? deriveProgress(ot?.consolidated, items) : null), [consolidated, ot, items]);
  const learner = useMemo(() => learnerView(progress, processPos), [progress, processPos]);
  useEffect(() => { if (learner) onboardingSignal("phase", { sessionId, index: learner.index, center: CENTERS[learner.index] ? CENTERS[learner.index].key : "" }); }, [learner && learner.index, sessionId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const refineOpen = !!(learner && learner.refineOpen);
  // MOVE ON → the student's progression decision (ANTI-TRAP: always available, never gated on TWS's judgment). ONE ACTIVE PROCESS AUTHORITY:
  // Move on ALWAYS runs the EXISTING set-aside event — the backend sets the current condition aside (stored, never "solved", no longer
  // blocking at any locus), the controller decides what the writing needs next and BEGINS it (a coach act, or the refinement episode itself).
  // The displayed step then FOLLOWS the controller's selected process (position → null): displayed process = controller process = the act.
  // Never a locally incremented step the controller did not choose.
  const progressMoveOn = useCallback(() => {
    if (!learner) return;
    setProcessPos(null);
    runCoach("", SET_ASIDE_EVENT);
  }, [learner, runCoach, setProcessPos]);
  // STUDENT NAVIGATION (view only — never the controller): a per-session history of where the student was (kind of work + left view). A click on
  // the strip pushes the current place and opens the destination; the Essay/Structure tabs push too; BACK pops — "return to where I was
  // immediately before this", never "the label to the left". No event, no turn, no set-aside, no provenance: TWS's recommendation is unchanged.
  const [navHistory, setNavHistory] = useState([]);
  const pushNav = useCallback(() => setNavHistory((h) => [...h, { pos: processPos, view }].slice(-24)), [processPos, view]);
  const navigateView = useCallback((v) => { if (v !== view) { pushNav(); setView(v); } }, [view, pushNav, setView]);
  const navigateKind = useCallback((key) => {
    const dest = DESTINATION_OF_CENTER[key];
    if (!dest) return;
    pushNav();
    setProcessPos(centerIndex(key));
    setView(dest);
    if (composeFocus) composeReturn(composeFocus);                    // leave the focused part so the destination is actually in view (the existing return)
  }, [pushNav, setProcessPos, setView, composeFocus, composeReturn]);
  const navBack = useCallback(() => {
    setNavHistory((h) => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setProcessPos(prev.pos); setView(prev.view);
      return h.slice(0, -1);
    });
  }, [setProcessPos, setView]);
  const progressReturn = useCallback((id) => runCoach("", returnEvent(id)), [runCoach]);   // COME BACK TO IT — explicit, the existing return event
  // THE FOCUSED COMPOSITION WORKSPACE (INCREMENT 4 — "Working on this part"): while the CURRENT FUNCTIONAL UNIT is being worked on (the
  // server-owned, intentional compose focus = the compose target the controller or the student opened), the LEFT workspace changes grain —
  // that unit's prose, its compact functional map, the one conversation on the right. The working object is the backend's target
  // (`target.included_unit_ids`), never chosen here; its location in the essay may be one paragraph or several. Back is navigation on the
  // EXISTING compose return route: no coach turn, nothing declared finished, the essay stays authoritative. No paragraph-to-paragraph walk.
  const focusIncludedKey = ((composeView && composeView.target && composeView.target.included_unit_ids) || ((((ot?.compose || {})[composeFocus] || {}).target || {}).included_unit_ids) || []).join(",");
  const focusNav = useMemo(() => {
    if (!consolidated || !composeFocus) return null;
    const included = focusIncludedKey ? focusIncludedKey.split(",") : [composeFocus];
    return unitLocation(doc, items, included) || unitLocation(doc, items, [composeFocus]);
  }, [consolidated, composeFocus, doc, items, focusIncludedKey]);
  const focusUnit = useMemo(() => (focusNav ? items.find((i) => i.id === composeFocus) || null : null), [focusNav, items, composeFocus]);
  const focused = !!(focusNav && focusUnit);
  const dismissOrientation = useCallback((k) => wsBigPictureClose(sessionId, k), [sessionId]);   // the student closed a TWS-opened Big Picture → that key is dismissed (presentation memory only)
  const emergingWhole = useEmergingWhole(sessionId, ot, consolidated, wsEmergingWhole, dismissOrientation);   // derived projection of the whole — re-read on every applied record
  bigPictureAttentionRef.current = emergingWhole.requestAttention;
  bigPictureOpenRef.current = emergingWhole.open;
  const bigPictureMode = !!(consolidated && emergingWhole.open && !focused);   // BIG PICTURE MODE: the whole-orientation workspace takes the full width; the dialogue panel belongs to the working workspace
  // RETURN TO ESSAY — the ONE navigation action back to the Essay workspace: closes the Big Picture (reporting a TWS-opened key as dismissed, the
  // existing close) and leaves Structure. Both Return controls (the header one and the one beneath the Big Picture) invoke exactly this.
  const returnToEssay = useCallback(() => { if (emergingWhole.open) emergingWhole.close(); if (view !== "essay") navigateView("essay"); }, [emergingWhole, view, navigateView]);
  // THE WORKING SPAN = the authoritative unit's paragraphs (guarded projection; never chosen here) + the DCO's verbatim target for this turn
  const focusSpan = useMemo(() => (focusNav ? workingSpan(items, focusNav.ids) : null), [focusNav, items]);
  const focusTargetText = useMemo(() => (composeView ? currentFocusText(composeView.dialogue, composeFocus) : ""), [composeView, composeFocus]);
  const focusReturnRef = useRef(null);
  const focusBack = useCallback(async () => { focusReturnRef.current = composeFocus; await composeReturn(composeFocus); }, [composeFocus, composeReturn]);
  // MOVE ON from the focused part (ANTI-TRAP): the words were already saved by the workspace; the compose episode closes cleanly (the EXISTING return
  // route — the unit's Composition state, dialogue and registry entry stay); then the EXISTING set-aside cascade runs (SET_ASIDE_EVENT): TWS's
  // unresolved judgment on this part is kept privately (a standing set-aside, never "complete"), the controller reads the writing and decides what
  // comes next. Nothing here chooses the next paragraph / unit / stage. Available regardless of TWS's judgment or the requested revision.
  const focusMoveOn = useCallback(async () => {
    focusReturnRef.current = composeFocus;
    await composeReturn(composeFocus);
    setProcessPos(null);                                                       // the displayed step follows the controller's next decision
    runCoach("", SET_ASIDE_EVENT);
  }, [composeFocus, composeReturn, runCoach, setProcessPos]);
  useEffect(() => { if (focused) setAssignmentOpen(false); }, [focused]);   // the assignment steps back while a part is being worked on (still one click away)
  useEffect(() => {
    if (composeFocus || !focusReturnRef.current) return;
    const id = focusReturnRef.current; focusReturnRef.current = null;
    const t = setTimeout(() => { try { document.querySelector(`[data-testid="essay-p-${id}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ } }, 80);
    return () => clearTimeout(t);
  }, [composeFocus]);
  const deferredQuestion = useCallback((e) => {
    const a = (ot?.dev_agenda || []).find((x) => x.key === e.key && x.status === "deferred");
    return ((a && a.deferred_question) || "").replace(/\*\*/g, "").trim();
  }, [ot]);

  const itemText = useCallback((id) => items.find((i) => i.id === id)?.text || "", [items]);
  const itemLabel = useCallback((id) => { const it = items.find((i) => i.id === id); return (it?.label || "").trim() || it?.text || ""; }, [items]);

  // Usability pass — a BRIEF, deterministic structural confirmation (no praise, no lecture, no LLM). Orients
  // the student to what just changed in the essay so they learn what the hierarchy MEANS while using it.
  const pushLocalTws = useCallback((text) => {
    setThread((t) => [...t, { id: `struct-${Date.now()}`, role: "tws", focus: [], focusQ: [], text }]);
  }, []);

  // Usability pass (§5/§6) — turn the coach's requested structural judgment into the ONE obvious action, on
  // the exact cards, in writing language. The student still makes the judgment (they tap it or decline); the
  // interface performs the diagram mechanic and briefly confirms what changed. Returns {label, run} or null.
  const awaitingAction = useCallback((a) => {
    if (!a || !a.operation) return null;
    const has = (id) => items.some((i) => i.id === id);
    const src = (a.source_ids || []).filter(has);
    const tgt = (a.target_ids || []).filter(has);
    const lbl = (id) => { const it = items.find((i) => i.id === id); return (it?.label || "").trim() || (it?.text || "").slice(0, 32); };
    const finish = (confirm) => { setAwaiting(null); if (confirm) pushLocalTws(confirm); runCoach("", "operation_done"); };
    const st = a.stated || {};
    // same-whole: when both referents already sit in one TWS-suggested part, the remaining act is to NAME that part
    // (it becomes the student's); otherwise the student's name forms a new part with both inside.
    const formWhole = async (name) => {
      const r = tgt.length === 1
        ? await wsPatchItem(sessionId, tgt[0], { label: name, label_authored: true })
        : await wsMakePart(sessionId, name, src);
      if (r?.ot) applyOt(r.ot);
    };
    switch (a.operation) {
      // CORRECTION #4 — the operation that REMAINS after a structural statement in words.
      case "name_whole":
        // the student recognised a same-whole relation but has not NAMED the whole: they name it, then the part forms
        if (src.length >= 2) return { kind: "name_whole", label: tgt.length === 1 ? "Name this part" : "Put both under it", run: async (name) => {
          const nm = (name || "").trim(); if (!nm) return;
          await formWhole(nm);
          finish(`"${lbl(src[0])}" and "${lbl(src[1])}" now sit under "${nm}" — your larger idea.`); } };
        return null;
      case "represent_whole":
        // the student ALREADY named the whole in words — never ask them to type it again
        if (src.length >= 2 && st.superordinate) return { label: tgt.length === 1 ? `Call this part "${st.superordinate}"` : `Put both under "${st.superordinate}"`, run: async () => {
          await formWhole(st.superordinate);
          finish(`"${lbl(src[0])}" and "${lbl(src[1])}" now sit under "${st.superordinate}".`); } };
        return null;
      // (no "represent_relation" / connection widget: relationships are asked about in plain language and
      //  represented by TWS from the student's answer — correction, human test)
      case "make_section":
        if (src.length === 1) return { label: "Make this a section", run: async () => { await mmMakeSection(src[0], "section", "body", false); finish(`"${lbl(src[0])}" is now a section.`); } };
        return null;
      case "make_subsection":
        if (src.length === 1) return { label: "Make this a subsection", run: async () => { await mmMakeSection(src[0], "subsection", "", false); finish(`"${lbl(src[0])}" is now a subsection.`); } };
        return null;
      case "change_to_idea":
        if (src.length === 1) return { label: "Change this to an idea", run: async () => { await mmMakeSection(src[0], "idea", "", false); finish(`"${lbl(src[0])}" is an idea again.`); } };
        return null;
      case "put_in":
        if (src.length >= 1 && tgt.length === 1) return { label: `Put in "${lbl(tgt[0])}"`, run: async () => { for (const s of src) await applyStructuralMove({ type: "idea", id: s }, { type: "idea", id: tgt[0] }); finish(`Now inside "${lbl(tgt[0])}".`); } };
        return null;
      case "keep_together":
        if (src.length >= 2) return { label: "Keep these together", run: async () => { for (const s of src.slice(1)) await applyStructuralMove({ type: "idea", id: s }, { type: "idea", id: src[0] }); finish("Those ideas are now grouped together."); } };
        return null;
      case "move_out":
        if (src.length === 1) return { label: "Move this out", run: async () => { await pullOut({ type: "idea", id: src[0] }); finish(`"${lbl(src[0])}" moved out.`); } };
        return null;
      default:
        return null;
    }
  }, [items, mmMakeSection, applyStructuralMove, pullOut, pushLocalTws, runCoach, sessionId, applyOt]);

  // CORRECTION #4 — input for the operation the student still has to perform in words (name the whole).
  // Reset whenever TWS hands over a new operation.
  const [wholeName, setWholeName] = useState("");
  useEffect(() => { setWholeName(""); }, [awaiting]);

  const qText = useCallback((id) => questions.find((q) => q.id === id)?.text || "", [questions]);
  const roots = items.filter((i) => !i.parent_id);
  const childrenOf = (id) => items.filter((i) => i.parent_id === id);
  const canSeeWhole = !!struct.can_see_whole;
  const nStudentIdeas = items.filter((i) => i.source === "student").length;
  const nStudentQuestions = questions.filter((q) => q.source === "student").length;
  const hasAgenda = nStudentQuestions >= 1; // once there's any student question, the "develop my ideas" action is discoverable
  // Guided three-phase problem representation: the student works ONE question type at a time
  // (explicit -> foundational -> implied); TWS decides readiness and advances the phase. The
  // whole question structure must exist before MY IDEAS opens (the ideas need those questions).
  const PHASES = ["explicit", "foundational", "implied"];
  const phaseIdx = PHASES.indexOf(feiPhase);
  const isRevealed = (p) => feiPhase === "done" || PHASES.indexOf(p) <= (phaseIdx < 0 ? 2 : phaseIdx);
  const qByFei = (p) => questions.filter((q) => q.fei === p);
  const showMap = qByFei("explicit").length > 0 && (qByFei("foundational").length + qByFei("implied").length) > 0;
  const showIdeas = feiPhase === "done" || nStudentIdeas > 0;
  const showOrganizeControls = nStudentIdeas >= 2;
  // MY THESIS becomes salient when enough relational organization exists for an overall answer to be meaningful.
  // v1.3 §8: the integrated "what I now think" emerges only once thinking is sufficiently organized —
  // not as an early form field. It then becomes the higher-order node atop the diagram.
  const showThesis = struct.sufficient || !!thesis.trim();
  const understandingOnly = !showIdeas;
  const lessonActive = understandingOnly && !lessonDone; // the full 3-kind teaching sequence is showing (TWS quiet)
  // CONSOLIDATION ROUND: the legacy student-facing question-path UI ("Understanding the assignment",
  // "Help me understand these questions", the three-question-type pathway) is HIDDEN — it implied a required
  // stage. All underlying code (renderPhaseSection, PHASES, question capture/differentiation, assignment
  // appraisal) is retained; questions remain dynamically available whenever holistic appraisal finds question
  // work productive. Flip this flag to true to restore the legacy UI.
  const showLegacyQuestionUI = false;
  // CLEANUP (2026-06): the obsolete student-facing orientation controls "Review: Understanding the
  // Question" and the current "How This Works" popup are HIDDEN. Their state (reviewOpen/helpOpen) and
  // overlay markup are retained DORMANT and recoverable — flip this flag to true to restore the buttons.
  const showLegacyOrientation = false;
  // ONE-TIME ORIENTATION ("How This Works"): NOT on entry and NOT the instant the first idea is submitted —
  // that would interrupt the first developmental cycle (student contributes → TWS responds → student
  // experiences how the workspace works). Fire once, at a natural break AFTER the student has completed an
  // initial exchange (≥1 of their own contributions + ≥1 TWS response, dialogue settled), with a short beat
  // so they take in the response first. Always also available on demand via the "How this works" button.
  useEffect(() => {
    if (!showLegacyOrientation) return; // CLEANUP (2026-06): "How This Works" orientation is hidden
    if (typeof window === "undefined") return;
    if (localStorage.getItem("tws_stage3_help_seen")) return;
    if (coachBusy) return; // never mid-dialogue
    const studentTurns = thread.filter((m) => m.role === "student").length;
    const twsResponses = thread.filter((m) => m.role === "tws" && m.id !== "welcome").length;
    if (studentTurns >= 1 && twsResponses >= 1) {
      localStorage.setItem("tws_stage3_help_seen", "1"); // guard immediately so it stays one-time
      const t = setTimeout(() => setHelpOpen(true), 2500); // let them experience the exchange first
      return () => clearTimeout(t);
    }
  }, [thread, coachBusy]);

  // JOURNEY PROGRESS (orientation layer) — reads existing deterministic OT state only, never drives it.
  // Stage 1 Understanding the Question = lesson step; 2 Understanding Your Assignment = fei phase (category
  // of 3); 3 Organizing Your Thoughts = answered-questions / sufficiency. Later stages live in PublicPreview.
  const _jPhaseIdx = ["explicit", "foundational", "implied"].indexOf(feiPhase);
  const journeyTitle = "Develop & Organize My Thinking";
  // AUTHENTIC REPAIR 1: no explanatory subtitle — the workspace shows the developing writing and structure itself.
  // (The former per-stage progress thermometer was replaced by the Orientation Layer — no percentages/step counts.)

  // recursive idea render (plain function — NOT a self-referential component; that trips the babel visual-edits plugin)
  const renderIdea = (item, depth) => {
    const kids = childrenOf(item.id);
    const isQuestion = item.status === "need_to_figure_out";
    const isTws = item.source === "tws";
    const focused = focusIds.includes(item.id);
    const isConnectTarget = connecting && connecting !== item.id;
    const isGroupTarget = grouping && grouping !== item.id;
    const isEditing = editing?.id === item.id;
    const isSeparating = separating?.id === item.id;
    const linked = item.question_ids || [];
    return (
      <div key={item.id} style={{ marginLeft: depth ? 18 : 0 }}>
        <div
          data-testid={`idea-${item.id}`}
          className={`group rounded-lg border px-3 py-2.5 transition-colors ${focused ? "border-[#8C3A2A] ring-2 ring-[#8C3A2A]/20" : "border-stone-200"} ${isQuestion ? "bg-amber-50/60" : "bg-white"} ${depth ? "border-l-2 border-l-stone-300" : ""}`}
        >
          {isEditing ? (
            <div>
              <textarea autoFocus data-testid="idea-edit-input" {...otNoPaste} value={editing.text}
                onChange={(e) => setEditing({ ...editing, text: e.target.value })} rows={2}
                className="w-full resize-none text-[14px] text-stone-800 outline-none border border-[#8C3A2A]/40 rounded p-2" />
              <div className="flex justify-end gap-2 mt-1.5">
                <button onClick={() => setEditing(null)} className="text-[12px] text-stone-500 px-2">Cancel</button>
                <button data-testid="idea-edit-save" onClick={saveEdit} className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[12px] px-3 py-1 rounded"><Check className="h-3 w-3" /> Save</button>
              </div>
            </div>
          ) : isSeparating ? (
            <div data-testid="separate-editor">
              <p className="text-[12px] text-stone-500 mb-2">Pull this into separate ideas — one per line:</p>
              {separating.parts.map((p, idx) => (
                <input key={idx} data-testid={`separate-part-${idx}`} {...otNoPaste} value={p}
                  onChange={(e) => { const parts = [...separating.parts]; parts[idx] = e.target.value; setSeparating({ ...separating, parts }); }}
                  placeholder={`Idea ${idx + 1}`} className="w-full text-[13.5px] px-2 py-1.5 mb-1.5 border border-stone-300 rounded outline-none focus:border-[#8C3A2A]" />
              ))}
              <div className="flex items-center justify-between mt-1">
                <button onClick={() => setSeparating({ ...separating, parts: [...separating.parts, ""] })} className="text-[12px] text-[#8C3A2A]">+ another</button>
                <div className="flex gap-2">
                  <button onClick={() => setSeparating(null)} className="text-[12px] text-stone-500 px-2">Cancel</button>
                  <button data-testid="separate-commit" onClick={commitSeparate} disabled={separating.parts.filter((x) => x.trim()).length < 2}
                    className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[12px] px-3 py-1 rounded disabled:opacity-40"><Scissors className="h-3 w-3" /> Separate</button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-2">
                {isQuestion && <HelpCircle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />}
                <p onClick={() => setSelectedIdeaId(selectedIdeaId === item.id ? null : item.id)}
                   className="flex-1 text-[14px] leading-snug text-stone-800 cursor-pointer">{item.text}</p>
                {isTws && <span className="shrink-0 text-[9px] uppercase tracking-wider text-stone-400 border border-stone-200 rounded px-1 py-0.5">TWS</span>}
              </div>
              {/* which question(s) this idea bears on — ideas are NOT trapped under one */}
              {questions.length > 0 && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {linked.map((qid) => (
                    <span key={qid} className="inline-flex items-center gap-1 text-[10px] text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5">
                      {(qText(qid) || "").slice(0, 22)}{(qText(qid) || "").length > 22 ? "…" : ""}
                    </span>
                  ))}
                  <button data-testid={`idea-qlink-${item.id}`} onClick={() => setLinkOpen(linkOpen === item.id ? null : item.id)}
                    className="inline-flex items-center gap-0.5 text-[10px] text-stone-400 hover:text-[#8C3A2A] border border-dashed border-stone-300 rounded-full px-1.5 py-0.5">
                    <Link2 className="h-2.5 w-2.5" /> {linked.length ? "edit" : "link to a question"}
                  </button>
                </div>
              )}
              {linkOpen === item.id && questions.length > 0 && (
                <div data-testid={`idea-qmenu-${item.id}`} className="mt-1.5 rounded-md border border-stone-200 bg-stone-50 p-2 space-y-1">
                  {questions.map((q) => (
                    <label key={q.id} className="flex items-center gap-2 text-[12px] text-stone-700 cursor-pointer">
                      <input type="checkbox" data-testid={`idea-qchip-${item.id}-${q.id}`} checked={linked.includes(q.id)}
                        onChange={() => toggleIdeaQuestion(item.id, q.id)} className="accent-[#8C3A2A]" />
                      {q.text}
                    </label>
                  ))}
                </div>
              )}
              {(selectedIdeaId === item.id || isConnectTarget || isGroupTarget || connecting || grouping) && (
              <div className="flex flex-wrap items-center gap-1 mt-2">
                {isConnectTarget ? (
                  <button data-testid={`connect-target-${item.id}`} onClick={() => setRelDraft({ from_id: connecting, to_id: item.id, text: "" })} className="text-[11px] font-medium text-white bg-[#8C3A2A] px-2 py-1 rounded">Connect here</button>
                ) : isGroupTarget ? (
                  <button data-testid={`group-target-${item.id}`} onClick={() => commitGroup(item.id)} className="text-[11px] font-medium text-white bg-amber-600 px-2 py-1 rounded">Make part of this</button>
                ) : (
                  <>
                    <ActBtn testid={`develop-${item.id}`} icon={Pencil} label="Develop" onClick={() => setEditing({ id: item.id, text: item.text })} />
                    {showOrganizeControls && <>
                      <ActBtn testid={`separate-${item.id}`} icon={Scissors} label="Separate" onClick={() => setSeparating({ id: item.id, parts: ["", ""] })} />
                      <ActBtn testid={`connect-${item.id}`} icon={Link2} label="Connect to…" onClick={() => setConnecting(item.id)} />
                      <ActBtn testid={`group-${item.id}`} icon={CornerDownRight} label="Make part of…" onClick={() => setGrouping(item.id)} />
                      {item.parent_id && <ActBtn testid={`ungroup-${item.id}`} icon={X} label="Move out" onClick={() => unGroup(item.id)} />}
                    </>}
                    <ActBtn testid={`uncertain-${item.id}`} icon={HelpCircle} label={isQuestion ? "Clearer now" : "Not sure"} onClick={() => toggleUncertain(item)} />
                    <ActBtn testid={`delete-${item.id}`} icon={Trash2} label="" onClick={() => deleteItem(item.id)} danger />
                  </>
                )}
              </div>
              )}
            </>
          )}
        </div>
        {kids.length > 0 && <div className="mt-2 space-y-2">{kids.map((k) => renderIdea(k, depth + 1))}</div>}
      </div>
    );
  };

  // A single question card during guided practice — edit / delete only (its FEI type is its section).
  const renderQCard = (q) => {
    const focused = focusQIds.includes(q.id);
    const isEditing = editingQ?.id === q.id;
    return (
      <div key={q.id} data-testid={`question-${q.id}`}
        className={`group rounded-lg border px-3 py-2 bg-white transition-colors ${focused ? "border-[#8C3A2A] ring-2 ring-[#8C3A2A]/20" : "border-stone-200"}`}>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input autoFocus data-testid="question-edit-input" {...otNoPaste} value={editingQ.text}
              onChange={(e) => setEditingQ({ ...editingQ, text: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") saveQEdit(); if (e.key === "Escape") setEditingQ(null); }}
              className="flex-1 text-[14px] px-2 py-1 border border-[#8C3A2A]/40 rounded outline-none" />
            <button data-testid="question-edit-save" onClick={saveQEdit} className="text-emerald-600"><Check className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <p className="flex-1 text-[14px] leading-snug text-stone-800">{q.text}</p>
            <span className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <ActBtn testid={`q-edit-${q.id}`} icon={Pencil} label="" onClick={() => setEditingQ({ id: q.id, text: q.text })} />
              <ActBtn testid={`q-delete-${q.id}`} icon={Trash2} label="" onClick={() => deleteQuestion(q.id)} danger />
            </span>
          </div>
        )}
      </div>
    );
  };

  // One representation step in the SINGLE evolving workspace. Full conceptual label, no E/F/I badge.
  // Hidden kinds (foundational/implied) always offer "I don't think I need one" — absence is valid.
  const renderPhaseSection = (p) => {
    const meta = PHASE_META[p];
    const m = FEI[p];
    const list = qByFei(p);
    const active = feiPhase === p;
    const complete = !active && (feiPhase === "done" || PHASES.indexOf(p) < (phaseIdx < 0 ? 3 : phaseIdx));
    const showInput = active || addOpen === p;
    const hidden = p !== "explicit";
    const nextPhase = p === "explicit" ? "foundational" : p === "foundational" ? "implied" : "done";
    const skipped = complete && list.length === 0;
    return (
      <div key={p} data-testid={`phase-${p}`}
        className={`rounded-lg px-4 py-3.5 transition-colors ${active ? "bg-white border border-[#8C3A2A]/25 shadow-sm" : "bg-transparent"} ${p !== "explicit" ? "border-t border-stone-200/70 rounded-none pt-4" : ""}`}>
        <div className="flex items-center gap-2">
          <span className={`inline-block h-2 w-2 rounded-full ${active ? m.dot : "bg-stone-300"}`} />
          <h3 className={`font-serif text-[15.5px] ${active ? "text-stone-900" : "text-stone-500"}`}>{meta.title}</h3>
          {complete && (
            <span data-testid={`phase-done-${p}`} className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-stone-400">
              {skipped ? "not needed" : <><Check className="h-3 w-3 text-emerald-600" /> noted</>}
            </span>
          )}
        </div>
        {active && <p className="mt-1.5 text-[13px] text-stone-600 leading-relaxed max-w-xl">{meta.prompt}</p>}
        {!active && !skipped && list.length > 0 && <p className="mt-1 text-[11.5px] text-stone-400">{meta.consolidated}</p>}
        {list.length > 0 && (
          <div className="mt-2.5 space-y-1.5">{list.map((q) => renderQCard(q))}</div>
        )}
        {showInput && (
          <div className="mt-3 flex items-start gap-2">
            <input data-testid={`add-question-input-${p}`} {...otNoPaste} value={qDraft[p] || ""}
              onChange={(e) => setQDraft((d) => ({ ...d, [p]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") addQuestion(p); }}
              placeholder={meta.placeholder} autoFocus={hidden && addOpen === p}
              className="flex-1 text-[14px] px-3 py-2 border border-stone-300 rounded-md outline-none focus:border-[#8C3A2A] bg-white" />
            <button data-testid={`add-question-btn-${p}`} onClick={() => addQuestion(p)} disabled={!(qDraft[p] || "").trim() || addingQ}
              className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[12px] font-medium px-3.5 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40">
              {addingQ ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add
            </button>
          </div>
        )}
        {active && (
          <div className="mt-3 flex flex-wrap items-center gap-4">
            {hidden && (
              <button data-testid={`skip-${p}`} onClick={() => advancePhase(nextPhase)}
                className="text-[12.5px] text-stone-500 hover:text-stone-800 underline underline-offset-2 decoration-stone-300">
                I don't think I need one
              </button>
            )}
            {list.length > 0 && (
              <button data-testid={`next-${p}`} onClick={() => advancePhase(nextPhase)}
                className="inline-flex items-center gap-1 text-[12.5px] font-medium text-[#8C3A2A] hover:text-[#6B2C20] ml-auto">
                {p === "explicit" ? "Next: what it takes for granted" : p === "foundational" ? "Next: check for completeness" : "See what I have to figure out"}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}
        {complete && !showInput && (
          <button data-testid={`add-more-${p}`} onClick={() => setAddOpen(p)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-[#8C3A2A]">
            <Plus className="h-3 w-3" /> add {list.length > 0 ? "another" : `a ${meta.noun}`}
          </button>
        )}
      </div>
    );
  };

  // v1.3.3: the single visual thinking map — thesis (integrating node) → question strands →
  // ideas/sub-ideas, with cross-branch connectors. UDWW 3C-4b: this is the STRUCTURE VIEW; the ESSAY VIEW is the same units read as one piece.
  const renderMap = (spaciousMap) => (
    <MindMap
      thesis={thesis} thesisStatus={thesisStatus} thesisRecognized={ot?.thesis?.student_judged === false}
      questions={questions} items={items} rels={rels} qrels={qrels}
      focusIds={focusIds} focusQIds={focusQIds} coachBusy={coachBusy}
      canSeeWhole={canSeeWhole} sufficient={struct?.sufficient} spacious={spaciousMap}
      regionsActive={true} thesisRegion={thesisRegion} onPlaceThesisRegion={placeThesisRegion}
      frame={consolidated ? "parts" : "essay"}
      teaching={consolidated ? (ot?.consolidated?.teaching || null) : null}
      onSplitParagraph={consolidated ? mmSplitParagraph : undefined} onJoinNext={consolidated ? mmJoinNext : undefined} refineOpen={refineOpen}
      latentStructure={latentStructure} descent={ot?.descent || null}
      onAdoptProvisional={adoptProvisional} onRejectProvisional={rejectProvisional}
      onDismissRole={dismissRole} onAdoptProvisionalRel={adoptProvisionalRel} onRejectProvisionalRel={rejectProvisionalRel}
      onSaveThesis={saveThesis} onWorkOn={workOnQuestion} onDevelop={mmDevelop}
      onSaveIdea={mmSaveIdea} onSaveQuestion={mmSaveQuestion} onTitleGiven={titleGiven} thesisProposal={thesisProposal}
      provisionalThesis={ot?.provisional_thesis || null} draftScope={sessionId}
      onStructuralMove={applyStructuralMove} onPullOut={pullOut} onDelete={deleteNode}
      onConnect={mmConnect} onDeleteRel={deleteRel} onSeparate={mmSeparate} onJoin={mmJoin} onMakeSection={mmMakeSection} onToggleUncertain={toggleUncertain}
      onCreateQuestion={onCreateQuestion} onCreateIdea={onCreateIdea}
      onAddLoose={(t) => onCreateIdea({ type: "loose" }, t)} onUndo={undoMove} canUndo={!!lastMove}
      onReorderSiblings={reorderSiblings} developSignal={developSignal}
      compose={composeShown} composeReady={ot?.compose_ready || {}} composeFocus={composeFocus}
      composeView={composeView} composeBusy={composeBusy}
      onComposeOpen={composeOpen} onComposeReturn={composeReturn} onComposeTurn={composeTurn}
    />
  );

  if (loading) {
    return <div data-testid="ot-workspace-loading" className="flex-1 flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>;
  }

  return (
    <div data-testid="thinking-workspace" className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 py-6">
      {/* CORRECTION #1 (authentic test): the cascade "You're working on" orientation strip is no longer shown in
          the thinking workspace (redundant with the coach + map; the cascade coordinator state is untouched and
          the strip still renders in the composition stage where its step-back target applies). */}
      {/* PHASE C3 — RETURN FROM WRITING: writing revealed something worth working out. This is NOT "go back
          and fix your map" — it's continuity. The essay is preserved; work the idea out here, then bring it
          back. Bounded to the relevant part(s); the whole thesis stays visible as orientation. */}
      {returnPurpose && (
        <div data-testid="ot-return-banner" className="mb-5 rounded-xl border border-sky-300 bg-sky-50/70 px-5 py-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sky-700 mb-1">Writing revealed something to work out</p>
          <p className="text-[14px] text-stone-800 leading-relaxed">
            As you were writing, you found something worth developing in your thinking. Work it out here, then bring it back into your essay.
          </p>
          {returnPurpose.developmental_problem ? (
            <p className="text-[13px] text-stone-700 mt-2"><span className="text-stone-500">What to work out:</span> {returnPurpose.developmental_problem}</p>
          ) : null}
          {(returnPurpose.relevant_labels || []).length > 0 ? (
            <p className="text-[12.5px] text-stone-600 mt-1"><span className="text-stone-500">Focus:</span> {returnPurpose.relevant_labels.join(" · ")}</p>
          ) : null}
          <button data-testid="ot-resume-composition" onClick={() => onResume?.()}
            className="mt-3 inline-flex items-center gap-2 text-[13px] font-medium px-4 py-2 rounded-md bg-[#8C3A2A] text-white hover:bg-[#6B2C20] transition-colors">
            <ArrowRight className="h-4 w-4" /> Bring this back to my writing
          </button>
        </div>
      )}
      <div className="mb-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-0">
            {!consolidated ? <h1 data-testid="journey-title" className="font-serif-display text-2xl sm:text-3xl leading-tight text-stone-900">{journeyTitle}</h1> : null}
            {/* THE PROCESS STRIP (consolidated Full Studio): the five writing processes at the top, the current one distinct — replaces the old title
                and the "Where I am" box. Orientation only; the progression decision lives in the action row beside Read my changes. */}
            {consolidated && progress && !lessonActive ? (
              <ProgressBlock progress={progress} position={processPos} disabled={coachBusy} quiet={focused}
                canBack={navHistory.length > 0} onBack={navBack} onNavigate={navigateKind} onReturn={progressReturn} />
            ) : null}
            {lessonActive && (
              <button data-testid="lesson-skip" onClick={() => setLessonDone(true)}
                className="mt-1.5 text-[12px] text-stone-400 hover:text-[#8C3A2A] underline underline-offset-2 decoration-stone-300">
                I already know how to do this — skip to my assignment
              </button>
            )}
          </div>
          <OrientationLayer signals={{
            sessionId, pathway: "full", screen: "ot", ready: !!ot,
            ot: {
              items: ot?.workspace?.items || [],
              relationships: ot?.workspace?.relationships || [],
              awaitingOp: awaiting?.op || "",
              devOp: ot?.dev_op?.operation || "",
              hasThesis: !!(ot?.thesis && (ot.thesis.text || "").trim()),
              thesisProposal: !!thesisProposal,
              regionsActive: !!regionsActive,
              consolidated, structureLicensed,   // ESSAY-AS-WORKSPACE: organizing begins when the controller licenses visible structure, not when a thesis is marked
              structureView: consolidated && view === "structure",   // the Structure view is open (intentionally): its first-use orientation, once
              composeFocused: consolidated && focused,               // Consolidate each part: a part is focused (first-use orientation, once)
              // ONLY THE OPENING EXISTS while organizing the whole: an introduction is placed and nothing beyond it is organized yet
              openingOnly: consolidated && progress?.center === "organize" && (ot?.workspace?.items || []).some((i) => !i.parent_id && (i.region || "") === "introduction" && (i.text || "").trim())
                && !(ot?.workspace?.items || []).some((i) => !i.parent_id && ["body", "conclusion"].includes(i.region || "") && (i.text || "").trim()),
            },
          }} onSendToCoach={sendToCoach} difficulty={difficulty} onDifficultyDone={difficultyDone} className="ml-auto shrink-0 pb-1" />
        </div>

      </div>

      {/* JUST-IN-TIME "Understanding the Question" lesson — optional Help, opens ABOVE the workspace and
          returns the student to their work intact (their assignment progress is never reset). */}
      {lessonHelpOpen && (
        <div data-testid="lesson-help-overlay" className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" onClick={() => setLessonHelpOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="mx-auto my-8 max-w-3xl">
            <div className="flex items-center justify-between mb-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/90">Understanding the question · a quick lesson</p>
              <button data-testid="lesson-help-close" onClick={() => setLessonHelpOpen(false)}
                className="inline-flex items-center gap-1 text-[12px] text-white/90 hover:text-white"><X className="h-4 w-4" /> Back to my assignment</button>
            </div>
            <UnderstandingLesson onDone={() => setLessonHelpOpen(false)} onProgress={() => {}} />
          </div>
        </div>
      )}

      <div className={`grid gap-5 items-start ${lessonActive || bigPictureMode ? "grid-cols-1" : understandingOnly ? "grid-cols-1 lg:grid-cols-[1.7fr_1fr]" : (dialogueCollapsed ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-[1.7fr_minmax(300px,0.85fr)]")}`}>
        {/* REPAIR §8: substantive thinking now ~60/40 — the TWS dialogue is a comfortable reading column (min 340px), not a narrow sidebar; map stays large enough to read the developing hierarchy. */}
        {/* MAIN: coordinated problem + thinking */}
        <div data-testid="workspace-main" className="min-w-0 space-y-6">
          {!understandingOnly && (
            <div data-testid="ot-assignment-panel" data-tour="assignment-panel"
              className="sticky top-2 z-20 rounded-xl border border-[#8C3A2A]/30 bg-[#faf6f4]/95 backdrop-blur px-4 py-3 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Target className="h-4 w-4 text-[#8C3A2A] shrink-0" />
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A]">Your assignment</p>
                {/* UDWW 3C-4b — ONE writing, two views (always reachable: this bar stays in view). STRUCTURE = the map of units;
                    ESSAY = the same units read as one piece. Access is never gated on pedagogical licensing. */}
                {showIdeas && !focused ? (
                  <div data-testid="view-toggle" className="ml-auto inline-flex items-center gap-2 text-[11px] font-medium">
                    {/* THE WORKSPACE IS ESSAY + COACH. Structure and Big Picture are TOOLS opened from Tools ▾ (or by TWS itself); Structure shows
                        "Return to Essay" while it is open. Access is never gated on pedagogical licensing; each click is student navigation (history, Back). */}
                    <button data-testid="view-essay" data-tour="view-essay" hidden={view !== "structure" && !bigPictureMode} onClick={returnToEssay}
                      className={`${view === "structure" || bigPictureMode ? "inline-flex" : "hidden"} items-center gap-1.5 rounded-full bg-[#8C3A2A] px-2.5 py-1 text-white transition-colors hover:bg-[#7a3324]`}>
                      <Eye className="h-3 w-3" /> Return to Essay
                    </button>
                    <ToolsMenu structureActive={view === "structure"} onStructure={() => navigateView("structure")} onBigPicture={() => { if (!emergingWhole.open) emergingWhole.toggle(); }} onScratch={() => { toggleScratch(true); setTimeout(() => { const el = document.querySelector('[data-testid="scratch-pad-textarea"]'); if (el) el.focus(); }, 50); }} />
                  </div>
                ) : null}
                <button data-testid="ot-assignment-toggle" onClick={() => setAssignmentOpen((o) => !o)}
                  className={`${showIdeas && !focused ? "" : "ml-auto "}text-[11px] font-medium text-stone-500 hover:text-[#8C3A2A] transition-colors`}>
                  {assignmentOpen ? "Hide" : "Show"}
                </button>
              </div>
              {assignmentOpen && (
                <p data-testid="ot-assignment-text" className="mt-1.5 text-[14px] leading-relaxed text-stone-900">
                  <HighlightedPrompt text={ot?.seed_assignment || ""} hl={highlight} />
                </p>
              )}
            </div>
          )}
          {/* CORRECTION #1 (authentic test): the "You're working on / Right now" summary banner was removed — redundant
              with the coach thread and the map focus. The active_field state is still received and used internally
              for map focus fallback (see pushTws). */}
          <>
              {pendingCaptures.length > 0 && (
                <div data-testid="pending-captures" className="space-y-2">
                  {pendingCaptures.map((c, idx) => (
                    <div key={idx} data-testid={`pending-capture-${idx}`} className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5">
                      <p className="text-[12px] text-amber-800 mb-1.5">Keep this as one of your ideas?</p>
                      <p className="text-[14px] text-stone-800 mb-2">“{c.text}”</p>
                      <div className="flex items-center gap-2">
                        <button data-testid={`keep-capture-${idx}`} onClick={() => keepCapture(idx)}
                          className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[12px] px-3 py-1 rounded-md hover:bg-[#6B2C20]"><Check className="h-3 w-3" /> Keep it</button>
                        <button data-testid={`dismiss-capture-${idx}`} onClick={() => dismissCapture(idx)}
                          className="text-[12px] text-stone-500 px-2 py-1 hover:text-stone-700">Not now</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {understandingOnly && (
                <section data-testid="understand-region" className="space-y-4">
                  {lessonActive && (
                    <UnderstandingLesson onDone={() => setLessonDone(true)} onProgress={setLessonProgress} />
                  )}
                  {!lessonActive && (
                  <>
                  <div className="rounded-xl border border-[#8C3A2A]/20 bg-[#8C3A2A]/[0.03] p-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-1.5">Your assignment</p>
                    <p data-testid="understand-prompt" className="text-[16px] leading-relaxed text-stone-900">
                      <HighlightedPrompt text={ot?.seed_assignment || ""} hl={highlight} />
                    </p>
                  </div>
                  <div data-testid="ua-try-first" className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-stone-200 bg-white px-3.5 py-2.5">
                    <p className="text-[12.5px] text-stone-600 flex-1 min-w-[220px]">Try this first — see which questions your assignment is asking. If you're not sure what these kinds of questions mean, I can show you.</p>
                    <button data-testid="help-understand-questions" onClick={() => setLessonHelpOpen(true)}
                      className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full border border-[#8C3A2A]/40 text-[#8C3A2A] hover:bg-[#8C3A2A]/5 transition-colors">
                      <HelpCircle className="h-3.5 w-3.5" /> Help me understand these questions
                    </button>
                  </div>
                  </>
                  )}
                </section>
              )}
              {/* --- REGION 1: QUESTIONS — ONE evolving problem-representation workspace --- */}
              {!lessonActive && understandingOnly && (
              <section data-testid="questions-region" className="space-y-3">
                <div>
                  <p className="text-[12px] text-stone-500 leading-relaxed max-w-xl">Start with the first kind: the question actually being asked. Add a question in the box below, or just tell me in the dialogue &mdash; either way I&rsquo;ll add it to your questions.</p>
                </div>
                <div className="rounded-xl border border-stone-200 bg-[#faf9f6] p-2 sm:p-3">
                  {PHASES.map((p) => (isRevealed(p) ? renderPhaseSection(p) : null))}
                </div>
                {/* TRANSITION A (agenda → developing ideas). Distinct from the later OT→FSC "Start developing
                    my writing" handoff. Student-controlled: once there's an agenda, a clear PRIMARY action is
                    always discoverable so the student is never stranded after judging the agenda sufficient. */}
                {hasAgenda ? (
                  <div data-testid="rep-sufficient" className="rounded-xl border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] p-4">
                    <p className="text-[13.5px] leading-relaxed text-stone-800 mb-3">
                      {repComplete || feiPhase === "implied"
                        ? "Now look at what you've done. You started with an assignment — now you've uncovered the questions your writing needs to address. These questions are going to become the map for your thinking. You don't have to get everything right now; as your thinking develops, you can come back, change a question, or discover another one."
                        : "Building an agenda of what you need to answer. When it feels like enough to begin, you can start developing your ideas — or keep working on the questions above first."}
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <button data-testid="start-developing-ideas" onClick={requestContinue}
                        className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2.5 rounded-md hover:bg-[#6B2C20]">
                        Start developing my thinking <ArrowRight className="h-4 w-4" />
                      </button>
                      <button data-testid="keep-working-agenda" onClick={() => setContinuePrompt(false)}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] transition-colors">
                        Keep working on what I need to answer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end pt-0.5">
                    <button data-testid="continue-to-thinking" onClick={requestContinue}
                      className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] transition-colors">
                      Start developing my thinking <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {continuePrompt && (
                  <div data-testid="continue-confirm" className="rounded-xl border border-amber-300 bg-amber-50/70 p-4 space-y-3">
                    <p className="text-[13.5px] leading-relaxed text-stone-800">
                      The Writing Studio thinks there may still be something to work out about what the assignment is asking.
                    </p>
                    <p className="text-[12.5px] leading-relaxed text-stone-600">
                      You can keep working on it now, or start developing your ideas. You can return here later.
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                      <button data-testid="continue-keep-working" onClick={() => setContinuePrompt(false)}
                        className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[12.5px] font-medium px-3.5 py-2 rounded-md hover:bg-[#6B2C20]">
                        Keep working on what I need to answer
                      </button>
                      <button data-testid="continue-anyway" onClick={() => proceedToThinking("continued_unresolved")}
                        className="inline-flex items-center gap-1.5 text-[12.5px] text-stone-600 hover:text-stone-900 underline underline-offset-2 decoration-stone-300">
                        Start developing my thinking
                      </button>
                    </div>
                  </div>
                )}
              </section>
              )}
              {/* v1.3 §3: once the problem is represented, FEI recedes into a compact reopenable control */}
              {showLegacyQuestionUI && !lessonActive && !understandingOnly && (
              <section data-testid="figure-out-region">
                <div className="flex flex-wrap items-center gap-2">
                  <button data-testid="figure-out-toggle" onClick={() => setFigureOpen((v) => !v)}
                    className="inline-flex items-center gap-2 text-[12.5px] text-stone-600 hover:text-stone-900 border border-stone-200 rounded-full px-3 py-1.5 bg-white">
                    <CompassIcon className="h-3.5 w-3.5 text-stone-400" />
                    <span className="font-medium">Understanding the assignment</span>
                    <span className="text-stone-400">{figureOpen ? "hide" : "open"}</span>
                  </button>
                  <button data-testid="help-understand-questions" onClick={() => setLessonHelpOpen(true)}
                    className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full border border-[#8C3A2A]/40 text-[#8C3A2A] hover:bg-[#8C3A2A]/5 transition-colors">
                    <HelpCircle className="h-3.5 w-3.5" /> Help me understand these questions
                  </button>
                </div>
                {figureOpen && (
                  <div className="mt-3 space-y-4 rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                    <p className="text-[12px] text-stone-600">What you have to answer — what the assignment directly asks (Asked), what it takes for granted that your reader may need (Taken for granted), and a question a complete answer still requires (Needed to fully answer). Edit these anytime.</p>
                    {PHASES.map((p) => (isRevealed(p) ? renderPhaseSection(p) : null))}
                    {showMap && <FeiMap questions={questions} items={[]} relationships={[]} focusIds={focusIds} />}
                  </div>
                )}
              </section>
              )}
              {/* relational work — available whenever TWS points at a pair, grounded in the visible map */}
              {/* DISABLED (narrow refinement): the "Working with these ideas" connection panel interrupts the
                  developmental conversation and is hard to parse. Relation DETECTION/appraisal (demoRelation/
                  relatePrompt state) and the student's ordinary map controls are preserved — only this UI
                  intervention is suppressed; the coach teaches relations conversationally instead. */}
              {false && !lessonActive && (demoRelation || relatePrompt) && (
                <div ref={relPanelRef} data-testid="relation-panel" className="rounded-xl border border-[#8C3A2A]/30 bg-[#8C3A2A]/[0.04] p-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-2">Working with these ideas</p>
                  {demoRelation ? (
                    <div data-testid="demo-relation">
                      <p className="text-[12.5px] text-[#6B2C20] mb-2">You've already connected these in what you said{demoRelation.student_grounding ? " — let's make it visible:" : ":"}</p>
                      <div className="flex items-center gap-2 text-[13px] text-stone-800 mb-1">
                        <span className="rounded bg-white border border-stone-200 px-2 py-1 max-w-[40%] truncate">{itemLabel(demoRelation.from_id)}</span>
                        <span className="text-[#8C3A2A] italic whitespace-nowrap">{demoRelation.relation_text || "connects to"} →</span>
                        <span className="rounded bg-white border border-stone-200 px-2 py-1 max-w-[40%] truncate">{itemLabel(demoRelation.to_id)}</span>
                      </div>
                      {demoRelation.student_grounding && <p className="text-[12px] text-stone-500 italic mb-2">“{demoRelation.student_grounding}”</p>}
                      <div className="flex items-center gap-2 mt-2">
                        <button data-testid="demo-confirm" onClick={confirmDemo}
                          className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[12px] px-3 py-1 rounded-md hover:bg-[#6B2C20]"><Check className="h-3 w-3" /> That's what I mean</button>
                        <button data-testid="demo-change" onClick={changeDemo} className="text-[12px] text-stone-600 px-2 py-1 hover:text-stone-900">Change the connection</button>
                        <button data-testid="demo-dismiss" onClick={() => setDemoRelation(null)} className="text-[12px] text-stone-400 px-2 py-1 hover:text-stone-600 ml-auto">Not this</button>
                      </div>
                    </div>
                  ) : (
                    <div data-testid="relate-prompt">
                      <p className="text-[12.5px] text-[#6B2C20] mb-2">The Writing Studio noticed two of your ideas may fit together — <span className="font-medium">you</span> decide how they connect:</p>
                      <div className="flex items-center gap-2 text-[13px] text-stone-700 mb-2">
                        <span className="rounded bg-white border border-[#8C3A2A]/40 px-2 py-1 max-w-[40%] truncate">{itemLabel(relatePrompt.from_id)}</span>
                        <span className="text-stone-400">?</span>
                        <span className="rounded bg-white border border-[#8C3A2A]/40 px-2 py-1 max-w-[40%] truncate">{itemLabel(relatePrompt.to_id)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <input data-testid="relate-input" {...otNoPaste} value={relInput} onChange={(e) => setRelInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") submitRelate(); }}
                          placeholder="How are these connected?  e.g. “leads to…”, “is the opposite of…”"
                          className="flex-1 text-[13px] px-3 py-2 border border-stone-300 rounded-md outline-none focus:border-[#8C3A2A] bg-white" />
                        <button data-testid="relate-submit" onClick={submitRelate} disabled={!relInput.trim()}
                          className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[12px] px-3 py-2 rounded-md hover:bg-[#6B2C20] disabled:opacity-40"><Link2 className="h-3 w-3" /> Connect</button>
                        <button data-testid="relate-prompt-dismiss" onClick={() => { setRelatePrompt(null); setRelInput(""); }} className="text-[12px] text-stone-500 px-2 py-1 hover:text-stone-700">Not related</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* --- REGION 2: THE THINKING MAP (revealed once the problem is understood) --- */}
              {showIdeas && (
              <section data-testid="ideas-region" className="space-y-3">
                {showLegacyOrientation && !canSeeWhole && (
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button data-testid="stage3-review-uq" onClick={() => setReviewOpen(true)} className="text-[12px] text-stone-500 hover:text-[#8C3A2A] px-2 py-1.5">Review: Understanding the Question</button>
                    <button data-testid="stage3-how-it-works" onClick={() => setHelpOpen(true)} className="inline-flex items-center gap-1 text-[12px] text-stone-500 hover:text-[#8C3A2A] px-2 py-1.5"><HelpCircle className="h-3.5 w-3.5" /> How this works</button>
                  </div>
                )}
                {/* AUTHENTIC REPAIR 1: no standing hint — the "Write a thought" input and the map show what to do */}
                {helpOpen && (
                  <div data-testid="stage3-help-modal" className="fixed bottom-4 right-4 z-50 w-[min(92vw,26rem)] pointer-events-auto">
                    <div className="rounded-2xl bg-white border border-stone-200 p-6 shadow-xl ring-1 ring-black/5">
                      <h3 className="font-serif text-2xl text-stone-900 mb-3">How This Works</h3>
                      <div className="space-y-2.5 text-[13.5px] text-stone-700 leading-relaxed">
                        <p>Organize your thinking with questions and answers.</p>
                        <p>Your questions show what you need to figure out. Your answers show what you currently think.</p>
                        <p>Start with any question. Add your answer beneath it. If an answer raises another question, put that question beneath the answer. Then answer that question too.</p>
                        <p>You can keep building this way: <span className="font-medium text-[#8C3A2A]">Question → Answer → Question → Answer</span></p>
                        <p>Move things around as your thinking changes. You do not have to work from top to bottom.</p>
                        <p className="text-stone-500 italic">You are not organizing your essay yet. You are organizing your thinking.</p>
                      </div>
                      <button data-testid="stage3-help-close" onClick={() => setHelpOpen(false)} className="mt-5 inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-5 py-2.5 rounded-md hover:bg-[#6B2C20]">Got it</button>
                    </div>
                  </div>
                )}
                {reviewOpen && (
                  <div data-testid="stage3-review-modal" className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" onClick={() => setReviewOpen(false)}>
                    <div onClick={(e) => e.stopPropagation()} className="max-w-lg w-full rounded-2xl bg-white border border-stone-200 p-6 shadow-xl">
                      <h3 className="font-serif text-2xl text-stone-900 mb-1">Understanding the Question</h3>
                      <p className="text-[12px] text-stone-500 mb-3">A quick reminder of the three kinds of questions an assignment can ask.</p>
                      <ol className="space-y-2 text-[13.5px] text-stone-700">
                        <li><span className="font-semibold">1 · The question being asked.</span> What the assignment directly asks you to answer.</li>
                        <li><span className="font-semibold">2 · Questions about what is taken for granted.</span> What it assumes you understand that your reader may not.</li>
                        <li><span className="font-semibold">3 · Questions about what is needed to fully answer.</span> What else a complete answer still requires.</li>
                      </ol>
                      <button data-testid="stage3-review-close" onClick={() => setReviewOpen(false)} className="mt-5 inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] font-medium px-5 py-2.5 rounded-md hover:bg-[#6B2C20]">Got it</button>
                    </div>
                  </div>
                )}
                {struct?.sufficient && view === "structure" && !focused && (
                  <p data-testid="sufficiency-nudge" className="text-[12px] text-[#8C3A2A]">You've worked out the major connections in your thinking — take a look at the whole.</p>
                )}
                {bigPictureMode ? (
                  <BigPictureWorkspace whole={emergingWhole.whole} from={view} reason={emergingWhole.reason} coach={emergingWhole.coach} onReturn={returnToEssay} />
                ) : focused ? (
                  <ParagraphFocus key={focusUnit.id} unit={focusUnit} location={focusNav.location} view={composeView} busy={composeBusy}
                    span={focusSpan} focusText={focusTargetText}
                    onBack={focusBack} onMoveOn={focusMoveOn}
                    onTurn={(text, kind, formatting) => composeTurn(focusUnit.id, text, kind, "", formatting)} onSave={(text, formatting) => mmSaveIdea(focusUnit.id, text, undefined, formatting)}
                    draftScope={sessionId} />
                ) : view === "essay" ? (
                  <EssayView doc={doc} items={items}
                    onSaveThesis={saveThesis} onSaveIdea={(id, text, formatting) => mmSaveIdea(id, text, undefined, formatting)}
                    onTitle={async (it, t) => { await mmSaveIdea(it.id, it.text, t); titleGiven(it, t); }}
                    compose={composeShown} composeReady={ot?.compose_ready || {}} composeFocus={composeFocus}
                    composeView={composeView} composeBusy={composeBusy}
                    onComposeOpen={composeOpen} onComposeReturn={composeReturn} onComposeTurn={composeTurn}
                    frame={consolidated ? "writing" : "essay"} structureLicensed={structureLicensed} refineOpen={refineOpen}
                    pendingThesis={(thesisProposal && thesisProposal.text) || ""}
                    onAddParagraph={consolidated ? addParagraph : undefined}
                    onMarkThesis={consolidated ? saveThesis : undefined}
                    onClearThesis={consolidated ? clearThesis : undefined} draftScope={sessionId}
                    onSetRegionStart={consolidated && structureLicensed ? setRegionStart : undefined}
                    coverage={coverage} onCoverage={consolidated ? confirmCoverage : undefined}
                    canUndo={!!lastMove} onUndo={undoMove} />
                ) : renderMap(false)}
              </section>
              )}
              {consolidated && !understandingOnly && !bigPictureMode && !focused ? (
                <ScratchPad sessionId={sessionId} open={scratchOpen} onToggle={() => toggleScratch()} onUseInEssay={useScratchInEssay} />
              ) : null}
            </>
        </div>

        {/* BELOW/BESIDE: grounded TWS dialogue — right column during problem representation, full width below the map while organizing.
            Hidden entirely during the demonstration lesson (no large inactive panel); restored, active, once the student works on their own assignment. */}
        {!lessonActive && !bigPictureMode && (
        <div data-testid="tws-dialogue-panel" data-tour="coach-dialogue" className="lg:sticky lg:top-4">
          <div className="rounded-lg border border-stone-200 bg-white flex flex-col" style={{ maxHeight: "calc(100vh - 8rem)" }}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100">
              <span className="grid place-items-center h-7 w-7 rounded-full bg-[#8C3A2A]/10 text-[#8C3A2A]"><CompassIcon className="h-4 w-4" /></span>
              <span className="font-serif text-[15px] text-stone-900">The Writing Studio</span>
              {(ot && !consolidated && wantsNewLoop()) ? (
                <span data-testid="tws-loop-mode-mismatch" title="This session is on the OLD pathway. Start a fresh session: ?studio=full&fresh=1" className="ml-auto font-mono text-[9px] uppercase tracking-[0.14em] text-rose-800 border border-rose-200 bg-rose-50 rounded px-1.5 py-px">old loop</span>
              ) : null}
            </div>
            <div data-testid="tws-thread" className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {lessonActive ? (
                <p data-testid="tws-quiet" className="text-[13px] text-stone-400 italic px-1 py-2">We'll try this with your assignment after the example.</p>
              ) : (<>
              <AnimatePresence initial={false}>
                {thread.map((m, mi) => (
                  <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={m.role === "student" ? "flex justify-end" : "flex justify-start"}
                    ref={m.role === "tws" && mi === thread.length - 1 ? lastTwsRef : undefined} data-thread-last-tws={m.role === "tws" && mi === thread.length - 1 ? "1" : undefined}>
                    <div data-testid={m.role === "tws" ? "tws-message" : "student-message"} {...(m.role === "student" ? otOwnCopy : {})}
                      className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-[13.5px] leading-relaxed ${m.role === "student" ? "bg-[#8C3A2A] text-white rounded-br-sm" : "bg-stone-100 text-stone-800 rounded-bl-sm"}`}>
                      {m.reflected && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#8C3A2A] mb-1"><Sparkles className="h-3 w-3" /> Notice</span>}
                      {m.role === "tws" && parseRefineReply(m.text)
                        ? <RefineReply text={m.text} />
                        : <p className={`${m.reflected ? "mt-0.5 " : ""}whitespace-pre-line`}>{m.role === "tws" ? renderTwsText(m.text) : m.text}</p>}
                      {m.role === "student" && consolidated && (m.text || "").split(/\s+/).length >= 12 && !items.some((it) => canonText(it.text) === canonText(m.text)) ? (
                        <button data-testid="student-message-incorporate" disabled={coachBusy} onClick={() => incorporateMessage(m)}
                          className="mt-2 inline-flex items-center gap-1 rounded-full border border-white/50 px-2.5 py-0.5 text-[11px] text-white/90 hover:bg-white/10 disabled:opacity-40">Add my words to essay</button>
                      ) : null}
                      {m.recovery ? (
                        <button data-testid={`turn-recovery-${m.recovery.status === "not_run" ? "resend" : "check"}`} disabled={coachBusy}
                          onClick={() => checkRecovery(m.recovery)}
                          className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#8C3A2A]/40 px-3 py-1 text-[12px] font-medium text-[#8C3A2A] hover:bg-[#8C3A2A]/5 disabled:opacity-40">
                          {TURN_RECOVERY_ACTION[m.recovery.status]}
                        </button>
                      ) : null}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              {coachBusy && (
                <div className="flex justify-start">
                  <div className="bg-stone-100 rounded-2xl px-3.5 py-2.5 flex items-center gap-2.5 max-w-[92%]">
                    <Loader2 className="h-4 w-4 animate-spin text-stone-400 shrink-0" />
                    {coachWait ? (
                      <span data-testid="coach-wait-status" data-stage={coachWait} className="text-[12.5px] text-stone-600 leading-snug">{coachWaitText(coachWait)}</span>
                    ) : null}
                  </div>
                </div>
              )}
              <div ref={threadEndRef} />
              </>)}
            </div>
            {awaiting && !lessonActive ? (
              <div data-testid="tws-awaiting-op" className="border-t border-amber-100 bg-amber-50/60 px-4 py-2.5">
                {awaiting.stated ? (
                  // CORRECTION #4 — the student's OWN structural statement + the resolved referents, so nobody has
                  // to remember what "these" meant. The map highlights the same two cards.
                  <div data-testid="stated-act" className="mb-2">
                    <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#8C3A2A]">You said</p>
                    {awaiting.stated.grounding ? (
                      <p data-testid="stated-act-grounding" className="text-[13px] text-stone-800 italic leading-snug">“{awaiting.stated.grounding}”</p>
                    ) : null}
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {(awaiting.source_ids || []).map((id, k) => (
                        <span key={id} data-testid={`stated-act-ref-${k}`}
                          className="inline-flex max-w-[260px] truncate rounded-md border border-[#8C3A2A]/40 bg-white px-2 py-0.5 text-[12px] text-stone-800">
                          {itemLabel(id)}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
                {awaiting.instruction ? <p data-testid="awaiting-instruction" className="text-[12.5px] text-stone-600 mb-2 leading-snug">{awaiting.instruction}</p> : null}
                {(() => {
                  const act = awaitingAction(awaiting);
                  const needsName = act && act.kind === "name_whole";
                  return (
                    <div className="flex flex-wrap items-center gap-2">
                      {needsName ? (
                        <input data-testid="stated-whole-name" {...otNoPaste} value={wholeName} onChange={(e) => setWholeName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter" && wholeName.trim()) { awaitingRef.current = null; act.run(wholeName); } }}
                          placeholder="Name the larger process these are both part of…"
                          className="flex-1 min-w-[220px] text-[13px] px-3 py-1.5 border border-stone-300 rounded-md outline-none focus:border-[#8C3A2A] bg-white" />
                      ) : null}
                      {act ? (
                        <button data-testid="tws-contextual-action" disabled={coachBusy || (needsName && !wholeName.trim())}
                          onClick={() => { awaitingRef.current = null; act.run(needsName ? wholeName : undefined); }}
                          className="inline-flex items-center gap-1.5 rounded-full bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-1.5 hover:bg-[#6B2C20] disabled:opacity-40 transition-colors">
                          <Check className="h-3.5 w-3.5" /> {act.label}
                        </button>
                      ) : null}
                      <button data-testid="tws-ive-done-this" disabled={coachBusy}
                        onClick={() => { selfDoneRef.current = true; setAwaiting(null); runCoach("", "operation_done"); }}
                        className={act
                          ? "inline-flex items-center gap-1.5 rounded-full border border-stone-300 text-stone-600 text-[12.5px] px-3.5 py-1.5 hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-40 transition-colors"
                          : "inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white text-[13px] font-medium px-4 py-1.5 hover:bg-emerald-700 disabled:opacity-40 transition-colors"}>
                        <Check className="h-3.5 w-3.5" /> {act ? "I did it myself" : "I've done this"}
                      </button>
                    </div>
                  );
                })()}
              </div>
            ) : null}
            {thesisProposal && !lessonActive ? (
              <div data-testid="provisional-thesis-scaffold" className="border-t-2 border-[#8C3A2A]/30 bg-[#8C3A2A]/[0.05] px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-1.5">A possible provisional thesis</p>
                <p className="text-[12px] text-stone-600 leading-snug mb-2">
                  A thesis is the central idea the rest of your essay develops, supports, or explains &mdash; and it&rsquo;s <em>provisional</em>, so it can change as your thinking grows. In your own words:
                </p>
                <p data-testid="provisional-thesis-candidate" className="text-[13.5px] text-stone-900 font-medium leading-snug border-l-2 border-[#8C3A2A] pl-2.5 mb-2.5">&ldquo;{thesisProposal.text}&rdquo;</p>
                <p className="text-[12px] text-stone-600 mb-2.5">{consolidated ? "It stays where you wrote it (it's underlined in your writing). " : ""}Does this express the main idea you want the rest of your points to belong under?</p>
                <div className="flex flex-col gap-2">
                  <button data-testid="pt-yes" onClick={acceptProvisionalThesis} disabled={coachBusy}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2 hover:bg-[#6B2C20] disabled:opacity-40 transition-colors">
                    <Check className="h-3.5 w-3.5" /> Use this as my provisional thesis
                  </button>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid="pt-not-quite" onClick={reviseProvisionalThesis} disabled={coachBusy}
                      className="rounded-full border border-stone-300 text-stone-700 text-[12.5px] px-3.5 py-1.5 hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-40 transition-colors">
                      Not quite &mdash; I want to change it
                    </button>
                    <button data-testid="pt-unsure" onClick={unsureProvisionalThesis} disabled={coachBusy}
                      className="rounded-full border border-stone-300 text-stone-500 text-[12.5px] px-3.5 py-1.5 hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-40 transition-colors">
                      I&rsquo;m not sure
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {regionOffer && !thesisProposal && !lessonActive ? (
              <div data-testid="region-offer-scaffold" className="border-t-2 border-[#8C3A2A]/30 bg-[#8C3A2A]/[0.05] px-4 py-3">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A] mb-1.5">Shape your thinking as an essay</p>
                <p className="text-[12px] text-stone-600 leading-snug mb-2.5">
                  Your thinking is taking the shape of an essay. We can start organizing this same map the way a reader will meet it &mdash; an <b>introduction</b>, a <b>body</b>, and a <b>conclusion</b>. Your ideas stay exactly as they are; you just decide where each part belongs.
                </p>
                <div className="flex flex-col gap-2">
                  <button data-testid="region-offer-yes" onClick={activateRegions} disabled={coachBusy}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#8C3A2A] text-white text-[13px] font-medium px-4 py-2 hover:bg-[#6B2C20] disabled:opacity-40 transition-colors">
                    <Check className="h-3.5 w-3.5" /> Yes, let&rsquo;s shape it as an essay
                  </button>
                  <button data-testid="region-offer-not-yet" onClick={declineRegions} disabled={coachBusy}
                    className="rounded-full border border-stone-300 text-stone-600 text-[12.5px] px-3.5 py-1.5 hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-40 transition-colors self-start">
                    Not yet &mdash; I want to keep developing
                  </button>
                </div>
              </div>
            ) : null}
            {latentStructure && (latentStructure.parts || []).length >= 2 && !thesisProposal && !regionOffer && !lessonActive ? (
              <div data-testid="latent-structure-note" className="border-t border-[#8C3A2A]/15 bg-[#8C3A2A]/[0.035] px-4 py-2.5 flex items-start gap-2">
                <Sparkles className="h-3.5 w-3.5 text-[#8C3A2A]/70 mt-0.5 shrink-0" />
                <p className="text-[11.5px] leading-snug text-stone-500 flex-1">
                  This is the shape I see your thinking taking so far &mdash; I put it together from your own ideas, nothing added. Reshape anything on the map as you keep going.
                </p>
                <button data-testid="latent-structure-clear" onClick={clearLatentStructure} disabled={coachBusy}
                  className="shrink-0 text-[11px] text-stone-400 hover:text-[#8C3A2A] underline underline-offset-2 disabled:opacity-40 transition-colors">Clear this view</button>
              </div>
            ) : null}
            {/* UDWW 3C-4b — MOVE ON: a quiet, obvious escape from the current developmental target (the backend's one deferral
                state); SET ASIDE FOR NOW appears only when something was actually deferred, visually secondary, with a return. */}
            {(liveTarget || deferredTargets.length > 0) && !consolidated && !lessonActive && !thesisProposal ? (
              <div data-testid="tws-target-controls" className="border-t border-stone-100 px-4 py-2 flex flex-wrap items-start gap-x-3 gap-y-1">
                {liveTarget ? (
                  <button data-testid="tws-move-on" disabled={coachBusy} onClick={moveOn}
                    title="Set aside what The Writing Studio just asked and go on to what comes next. It stays available for when you come back to it."
                    className="inline-flex items-center gap-1 rounded-full border border-stone-300 text-stone-500 text-[11.5px] px-3 py-1 hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-40 transition-colors">
                    <ArrowRight className="h-3 w-3" /> Move on for now
                  </button>
                ) : null}
                {deferredTargets.length > 0 ? (
                  <details data-testid="tws-deferred" className="basis-full min-w-0 text-[11.5px] text-stone-500">
                    <summary className="cursor-pointer select-none list-none inline-flex items-center gap-1 py-1 hover:text-[#8C3A2A]">
                      Set aside for now <span className="font-mono text-[9px] text-stone-400">({deferredTargets.length})</span>
                    </summary>
                    <ul className="mt-1 space-y-2">
                      {deferredTargets.map((e) => {
                        const uid = (e.ids || [])[0];
                        const unit = uid ? items.find((i) => i.id === uid) : null;
                        const qtext = deferredQuestion(e) || e.words || "";
                        return (
                          <li key={e.key} data-testid={`tws-deferred-${e.key}`} className="min-w-0 border-l-2 border-stone-200 pl-2 leading-snug">
                            {qtext ? <p className="italic text-stone-600 break-words">“{qtext}”</p> : <p className="italic">a question you set aside</p>}
                            {unit ? <p className="text-[10.5px] text-stone-400 truncate">about: {(unit.label || "").trim() || unit.text}</p> : null}
                            {unit ? (
                              <button data-testid={`tws-deferred-return-${e.key}`} disabled={coachBusy}
                                onClick={() => mmDevelop({ type: "idea", id: unit.id, text: unit.text })}
                                className="mt-0.5 text-[10.5px] font-medium text-[#8C3A2A] hover:underline disabled:opacity-40">Come back to this</button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </details>
                ) : null}
              </div>
            ) : null}
            {/* RELEASE TO WRITING — the return path (consolidated loop): the student writes in their essay, then clicks Read my changes;
                TWS reads the essay as it stands and says what it needs next. A bout of editing is over — never "the essay is finished";
                no workflow, no popup; never "type done". INCREMENT 4: hidden while a part is being worked on — there the ONE read action is
                "Read with TWS" on the part itself (the compose turn), so two competing read controls never appear for the same work. */}
            {consolidated && !thesisProposal && !focused ? (
              <div data-testid="tws-done-writing-row" className="px-3 pt-1 pb-2 flex items-center justify-end gap-2">
                {/* THE ACTION ROW: Read my changes (primary — "I changed this; read it") · Move on (secondary — "I'm finished with this for now;
                    continue"). One read action in this context; Move on always visible, never gated on TWS's judgment. */}
                <DoneWriting disabled={coachBusy} runCoach={runCoach} />
                {progress && !lessonActive ? (
                  <button type="button" data-testid="tws-progress-move-on" disabled={coachBusy} onClick={progressMoveOn} data-pending={coachBusy ? "1" : undefined}
                    title="I'm finished working on this for now — continue. The Writing Studio decides what comes next."
                    className="inline-flex items-center gap-1 rounded-full border border-stone-300 bg-white px-3 py-1.5 text-[12px] font-medium text-stone-600 hover:border-[#8C3A2A] hover:text-[#8C3A2A] disabled:opacity-60 transition-colors">
                    {coachBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Working…</> : <>Move on <ArrowRight className="h-3.5 w-3.5" /></>}
                  </button>
                ) : null}
              </div>
            ) : null}
            <div className="border-t border-stone-100 p-3 flex items-end gap-2">
              <textarea data-testid="tws-chat-input" data-tour="coach-input" ref={chatRef} value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                placeholder="Have a conversation with me here." rows={4}
                className="flex-1 resize-none text-[13.5px] leading-relaxed text-stone-800 placeholder:text-stone-400 outline-none min-h-[96px] max-h-56" />
              <button data-testid="tws-send-btn" data-tour="coach-send" onClick={sendChat} disabled={!chatInput.trim() || coachBusy}
                className="grid place-items-center h-9 w-9 rounded-full bg-[#8C3A2A] text-white disabled:opacity-40 hover:bg-[#6B2C20]"><Send className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
        )}
      </div>

      {relDraft && (
        <div className="fixed inset-0 z-40 grid place-items-center bg-stone-900/30 px-4" onClick={() => setRelDraft(null)}>
          <div data-testid="rel-label-dialog" onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
            <p className="font-serif text-lg text-stone-900 mb-1">How are these connected?</p>
            <p className="text-[13px] text-stone-600 mb-3"><span className="font-medium">{itemText(relDraft.from_id)}</span> → <span className="font-medium">{itemText(relDraft.to_id)}</span></p>
            <input autoFocus data-testid="rel-label-input" {...otNoPaste} value={relDraft.text} onChange={(e) => setRelDraft({ ...relDraft, text: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") commitRelationship(); }}
              placeholder="e.g. because, so, but, helps explain, is part of… (in your own words)"
              className="w-full text-[14px] px-3 py-2 border border-stone-300 rounded-md outline-none focus:border-[#8C3A2A]" />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRelDraft(null)} className="text-[13px] px-3 py-1.5 text-stone-500">Cancel</button>
              <button data-testid="rel-label-save" onClick={commitRelationship} className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[13px] px-4 py-1.5 rounded-md hover:bg-[#6B2C20]"><Link2 className="h-3.5 w-3.5" /> Connect</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ActBtn({ testid, icon: Icon, label, onClick, danger }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-1 rounded hover:bg-stone-100 transition-colors ${danger ? "text-stone-400 hover:text-rose-500" : "text-stone-500 hover:text-stone-800"}`}>
      <Icon className="h-3.5 w-3.5" />{label}
    </button>
  );
}

// v1.3 §3: render **bold** (and stray *emphasis*) in TWS messages so the core question stands out.
function renderTwsText(text) {
  const parts = String(text || "").split(/(\*\*[\s\S]+?\*\*)/g);
  const isBold = (p) => p.startsWith("**") && p.endsWith("**") && p.length > 4;
  // a bold run standing on a line of its own is a DB heading (What you've established / What to work on / Try this): rendered as a block
  const isHeading = (i) => isBold(parts[i]) && (i === 0 || /\n\s*$/.test(parts[i - 1] || "")) && /^\s*\n/.test(parts[i + 1] || "");
  return parts.map((p, i) => {
    if (isBold(p)) {
      const heading = isHeading(i);
      return <strong key={i} data-testid={heading ? "tws-heading" : undefined} className={heading ? "block font-semibold text-stone-900 mt-3 first:mt-0" : "font-semibold text-stone-900"}>{p.slice(2, -2).replace(/\*/g, "")}</strong>;
    }
    // strip any leftover single-asterisk emphasis markers so they never show as literal characters; a heading's own line break is the block
    const t = (i > 0 && isHeading(i - 1)) ? p.replace(/^\s*\n/, "") : p;
    return <span key={i}>{t.replace(/\*(\S[^*\n]*?\S|\S)\*/g, "$1")}</span>;
  });
}

// The evolving thinking diagram (§7/§8): the SAME FEI question structure, now GROWING as the
// student's ideas appear under the questions they answer. Cards are COMPACT (truncated, expand on
// click, "+N more") so the whole stays apprehensible; ideas TWS is pointing at are highlighted;
// represented relations are summarized. It shows only student content and leaves gaps visible.
function FeiMap({ questions, items = [], relationships = [], focusIds = [], spacious = false, integrated = "" }) {
  const [expanded, setExpanded] = useState({});
  const [showAll, setShowAll] = useState({});
  const g = (p) => questions.filter((q) => q.fei === p);
  const ideasFor = (qid) => items.filter((i) => (i.question_ids || []).includes(qid));
  const anyIdeas = items.some((i) => (i.question_ids || []).length > 0);
  const itemText = (id) => { const it = items.find((x) => x.id === id) || {}; return (it.label || "").trim() || it.text || "?"; };
  const toggle = (setter, key) => setter((s) => ({ ...s, [key]: !s[key] }));
  const IdeaChip = ({ i }) => {
    const isOpen = expanded[i.id];
    const focused = focusIds.includes(i.id);
    const label = (i.label || "").trim() || i.text;
    return (
      <button type="button" onClick={() => toggle(setExpanded, i.id)} data-testid={`map-idea-${i.id}`}
        title={i.text}
        className={`w-full text-left rounded-md border px-2 py-1 text-[11.5px] leading-snug transition-colors ${focused ? "border-[#8C3A2A] ring-2 ring-[#8C3A2A]/25 bg-[#8C3A2A]/[0.04] text-stone-900" : "border-stone-200 bg-white text-stone-700 hover:border-stone-300"}`}>
        {isOpen
          ? <span>{i.text}</span>
          : <span className="line-clamp-1 font-medium">{label}</span>}
      </button>
    );
  };
  const Lane = ({ p }) => {
    const m = FEI[p];
    const list = g(p);
    const tone = p === "explicit" ? "text-[#8C3A2A]" : p === "foundational" ? "text-amber-700" : "text-teal-700";
    return (
      <div data-testid={`map-lane-${p}`} className="min-w-0">
        <p className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${tone}`}>{m.label}</p>
        <div className="space-y-1.5">
          {list.length === 0
            ? <div className="rounded-md border border-dashed border-stone-300 px-2.5 py-2 text-[11px] text-stone-400 italic">none yet</div>
            : list.map((q) => {
                const qi = ideasFor(q.id);
                const seeAll = showAll[q.id];
                const shown = seeAll ? qi : qi.slice(0, 3);
                return (
                  <div key={q.id} data-testid={`map-q-${q.id}`} className="space-y-1">
                    <div className={`rounded-md border px-2.5 py-1.5 text-[12px] leading-snug ${m.cls}`}>{q.text}</div>
                    {qi.length > 0 ? (
                      <div className="ml-3 space-y-1 border-l-2 border-stone-200 pl-2">
                        {shown.map((i) => <IdeaChip key={i.id} i={i} />)}
                        {qi.length > 3 && (
                          <button data-testid={`map-more-${q.id}`} onClick={() => toggle(setShowAll, q.id)}
                            className="text-[10px] text-stone-400 hover:text-[#8C3A2A]">{seeAll ? "show fewer" : `+${qi.length - 3} more`}</button>
                        )}
                      </div>
                    ) : (anyIdeas && (
                      <div className="ml-3 pl-2 text-[10px] text-stone-400 italic">no idea here yet</div>
                    ))}
                  </div>
                );
              })}
        </div>
      </div>
    );
  };
  const Conn = ({ label, leftward }) => (
    <div className="flex flex-col items-center justify-center px-1">
      <span className="text-[9px] uppercase tracking-wider text-stone-400 text-center leading-tight mb-0.5 whitespace-nowrap">{label}</span>
      <ArrowRight className={`h-4 w-4 text-[#8C3A2A]/60 hidden md:block ${leftward ? "rotate-180" : ""}`} />
      <ArrowRight className="h-4 w-4 text-[#8C3A2A]/60 rotate-90 md:hidden" />
    </div>
  );
  return (
    <div data-testid="fei-map-live" className={`rounded-xl border border-stone-200 bg-[#faf9f6] ${spacious ? "p-6" : "p-4"}`}>
      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400 mb-1">{anyIdeas ? "My thinking so far" : "How these questions fit together"}</p>
      <p className="text-[11px] text-stone-500 mb-3">Your <span className="text-amber-700 font-medium">taken-for-granted</span> background sits behind the <span className="text-[#8C3A2A] font-medium">asked</span> question; your <span className="text-teal-700 font-medium">needed-to-fully-answer</span> questions are needed for a complete answer.{anyIdeas ? " Ideas sit under the questions they answer — tap one to read it in full." : ""}</p>
      {integrated && (
        <div data-testid="integrated-node" className="mb-4 rounded-lg border-2 border-[#8C3A2A] bg-[#8C3A2A]/[0.06] px-4 py-3">
          <p className="font-mono text-[9px] uppercase tracking-wider text-[#8C3A2A] mb-1">What I now think — everything below sits under this</p>
          <p className="text-[14px] leading-snug text-stone-900 font-medium">{integrated}</p>
          <div className="flex justify-center mt-1"><ArrowRight className="h-4 w-4 text-[#8C3A2A]/60 rotate-90" /></div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1.1fr_auto_1fr] gap-2 md:gap-3 items-start">
        <Lane p="foundational" />
        <Conn label="needed for" />
        <Lane p="explicit" />
        <Conn label="helps explain" leftward />
        <Lane p="implied" />
      </div>
      {relationships.length > 0 && (
        <div data-testid="map-relations" className="mt-4 pt-3 border-t border-stone-200">
          <p className="text-[9px] uppercase tracking-wider text-stone-400 mb-1.5">Connections you've made</p>
          <div className="space-y-1">
            {relationships.map((r) => (
              <div key={r.id} data-testid={`map-rel-${r.id}`} className="flex items-center flex-wrap gap-1.5 text-[11.5px] text-stone-600">
                <span className="rounded bg-white border border-stone-200 px-1.5 py-0.5 max-w-[38%] truncate">{itemText(r.from_id)}</span>
                <span className="text-[#8C3A2A] italic whitespace-nowrap">— {r.relation_text || "connects to"} →</span>
                <span className="rounded bg-white border border-stone-200 px-1.5 py-0.5 max-w-[38%] truncate">{itemText(r.to_id)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Teaching runs as ONE continuous sequence through all three kinds (Asked → Taken for Granted →
// Needed to Fully Answer). Only after all three are taught does onDone hand off to the student's
// actual-assignment activity, which then proceeds through the same three kinds one at a time.
const UA_FLOW = ["provoke", "simple", "four", "threekinds", "asked", "tfg", "needed"];

function UnderstandingLesson({ onDone, onProgress }) {
  const FLOW = UA_FLOW;
  const [idx, setIdx] = useState(0);
  const [guess, setGuess] = useState(null); // Screen-2 lightweight prediction (not scored/gated)
  const id = FLOW[idx];
  const next = () => { if (idx >= FLOW.length - 1) { onBlockDone && onBlockDone(); } else setIdx((i) => i + 1); };
  const back = () => setIdx((i) => Math.max(0, i - 1));
  const onBlockDone = onDone;
  useEffect(() => { onProgress && onProgress((idx + 1) / FLOW.length); }, [idx, FLOW.length, onProgress]);

  // ── shared instructional helpers ──
  const Eyebrow = ({ children }) => (<p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-2">{children}</p>);
  const Title = ({ children }) => (<h2 className="font-serif text-2xl sm:text-[32px] leading-[1.12] text-stone-900 max-w-2xl">{children}</h2>);
  const Lead = ({ children, className = "" }) => (<p className={`text-[15px] leading-relaxed text-stone-600 max-w-xl ${className}`}>{children}</p>);
  const Reveal = ({ children }) => (<p className="font-serif text-[20px] sm:text-[23px] leading-snug text-[#8C3A2A] max-w-xl">{children}</p>);
  const Kicker = ({ n, children }) => (
    <div className="flex items-center gap-3">
      <span className="grid place-items-center h-9 w-9 rounded-full bg-[#8C3A2A] text-white font-serif text-lg">{n}</span>
      <span className="font-mono text-[12px] uppercase tracking-[0.24em] text-[#8C3A2A]">{children}</span>
    </div>
  );
  const Note = ({ children }) => (<p className="text-[13px] leading-relaxed text-stone-500 max-w-xl">{children}</p>);
  const NextBtn = ({ label, onClick, testid }) => (
    <button data-testid={testid || "lesson-next"} onClick={onClick}
      className="inline-flex items-center gap-1.5 bg-[#8C3A2A] text-white text-[14px] font-medium px-5 py-3 rounded-md hover:bg-[#6B2C20] transition-colors">
      {label} <ArrowRight className="h-4 w-4" />
    </button>
  );

  // ── VISUAL GRAMMAR (distinct object types) ──
  // SOURCE = the assignment: largest, neutral, dominant, labelled ASSIGNMENT.
  const SourceCard = ({ children, testid }) => (
    <div data-testid={testid || "ua-source"} className="rounded-xl bg-stone-100 border-2 border-stone-400 px-5 py-5 max-w-2xl shadow-sm">
      <p className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-1.5 text-stone-500">Assignment</p>
      <p className="font-serif text-[22px] sm:text-[25px] leading-snug text-stone-900">{children}</p>
    </div>
  );
  // Vertical connector, optional italic label — communicates derivation, not equivalence.
  const Connector = ({ label }) => (
    <div className="flex flex-col items-center py-1.5 max-w-2xl">
      <ArrowRight className="h-5 w-5 rotate-90 text-stone-300" />
      {label && <span className="text-[11px] italic text-stone-400 mt-0.5">{label}</span>}
    </div>
  );
  // ASKED = derived directly from the source: terracotta, smaller, sits below/indented from source.
  const AskedCard = ({ children, testid }) => (
    <div data-testid={testid || "ua-asked"} className="ml-6 rounded-lg border border-[#8C3A2A]/40 bg-[#8C3A2A]/[0.05] px-4 py-3 max-w-lg">
      <p className="text-[9.5px] uppercase tracking-[0.16em] font-semibold mb-1 text-[#8C3A2A]">Asked</p>
      <p className="font-serif text-[17px] sm:text-[18px] leading-snug text-stone-900">{children}</p>
    </div>
  );
  // ASSUMED = background the Asked question takes for granted: amber, offset side-branch, dashed edge, subordinate.
  const AssumedCard = ({ children, testid }) => (
    <div data-testid={testid || "ua-assumed"} className="ml-14 border-l-2 border-dashed border-amber-400 pl-4 py-1 max-w-md">
      <p className="text-[9.5px] uppercase tracking-[0.16em] font-semibold mb-1 text-amber-700">Taken for granted · background</p>
      <div className="font-serif text-[15.5px] leading-snug text-stone-700 space-y-0.5">{children}</div>
    </div>
  );
  // NEEDED TO FULLY ANSWER = completion requirement inferred from what a full answer needs: teal, extension.
  const NeededCard = ({ children, testid }) => (
    <div data-testid={testid || "ua-needed"} className="ml-6 rounded-lg border-2 border-teal-400 bg-teal-50 px-4 py-3 max-w-lg">
      <p className="text-[9.5px] uppercase tracking-[0.16em] font-semibold mb-1 text-teal-700">Needed to fully answer</p>
      <div className="font-serif text-[17px] leading-snug text-stone-900 space-y-0.5">{children}</div>
    </div>
  );
  // DIAGNOSTIC / ASK-YOURSELF = an instructional cue, NOT a content object: thin rule, italic serif, no card.
  const Cue = ({ children, testid }) => (
    <div data-testid={testid || "ua-cue"} className="border-l-2 border-[#8C3A2A]/50 pl-4 max-w-xl">
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#8C3A2A]/70 mb-1">Ask yourself</p>
      <p className="font-serif italic text-[19px] sm:text-[20px] leading-snug text-stone-800">{children}</p>
    </div>
  );

  const DEMOC = "\u201CExplain the difference between democracy and autocracy and how decisions are made in each form of government.\u201D";

  const renderScreen = (sid) => {
    switch (sid) {
      case "provoke": return (
        <div className="space-y-6">
          <Eyebrow>Full Writing Pathway · Understand the assignment</Eyebrow>
          <p className="text-[18px] sm:text-[20px] text-stone-500 max-w-xl">Before you dig in, there&rsquo;s one thing that we just have to get right.</p>
          <h2 className="font-serif italic text-3xl sm:text-[40px] leading-[1.08] text-stone-900 max-w-2xl">What is the assignment really asking you to do?</h2>
          <Lead>Sounds obvious. Sometimes it is. But there&rsquo;s often more to an assignment than meets the eye.</Lead>
          <NextBtn label="Show me" onClick={next} testid="lesson-next" />
        </div>
      );
      case "simple": return (
        <div className="space-y-6">
          <Title>Take a look at this assignment.</Title>
          <SourceCard>{DEMOC}</SourceCard>
          <Lead>Seems pretty straightforward. <span className="font-medium text-stone-800">But how many questions do you actually have to answer?</span></Lead>
          <div className="flex items-center gap-2" data-testid="ua-guess">
            {["1", "2", "3", "4+"].map((g) => (
              <button key={g} data-testid={`ua-guess-${g}`} onClick={() => setGuess(g)}
                className={`h-11 min-w-[3rem] px-3 rounded-lg border-2 font-serif text-[18px] transition-colors ${guess === g ? "border-[#8C3A2A] bg-[#8C3A2A]/[0.06] text-[#8C3A2A]" : "border-stone-300 text-stone-600 hover:border-stone-400"}`}>
                {g}
              </button>
            ))}
          </div>
          <NextBtn label="Show me" onClick={next} testid="lesson-next" />
        </div>
      );
      case "four": return (
        <div className="space-y-5">
          <Title>There were four.</Title>
          <SourceCard>{DEMOC}</SourceCard>
          <Connector label="what's actually asked" />
          <div className="space-y-2" data-testid="ua-four">
            <AskedCard testid="ua-asked-0">What is the difference between democracy and autocracy?</AskedCard>
            <AskedCard testid="ua-asked-1">How are decisions made in a democracy?</AskedCard>
            <AskedCard testid="ua-asked-2">How are decisions made in an autocracy?</AskedCard>
            <AskedCard testid="ua-asked-3">How do those ways of making decisions differ?</AskedCard>
          </div>
          <Reveal>Miss one, and you&rsquo;ve missed part of the assignment.</Reveal>
          <Note>That&rsquo;s why understanding the assignment matters. Before you decide what to write, you need to know what your writing has to accomplish.</Note>
          <NextBtn label="There&rsquo;s more" onClick={next} testid="lesson-next" />
        </div>
      );
      case "threekinds": return (
        <div className="space-y-6">
          <Title>Some questions are easy to see. Others are hidden.</Title>
          <Reveal>When you read an assignment, there are three kinds of questions to look for.</Reveal>
          <div className="space-y-4 max-w-xl" data-testid="ua-threekinds">
            <div className="rounded-lg border border-[#8C3A2A]/40 bg-[#8C3A2A]/[0.05] px-4 py-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C3A2A] mb-1">1 · The question being asked</p>
              <p className="text-[14px] text-stone-700">What does the assignment actually ask you to answer?</p>
            </div>
            <div className="ml-4 border-l-2 border-dashed border-amber-400 pl-4 py-1">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700 mb-1">2 · Questions about what is taken for granted</p>
              <p className="text-[14px] text-stone-700">What does the assignment assume <span className="font-semibold">you</span> understand but that your <span className="font-semibold">reader</span> may not?</p>
            </div>
            <div className="rounded-lg border-2 border-teal-400 bg-teal-50 px-4 py-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] font-semibold text-teal-700 mb-1">3 · Questions about what is needed to fully answer</p>
              <p className="text-[14px] text-stone-700">What else do you need to address to fully answer what the assignment asks?</p>
            </div>
          </div>
          <Note>You won&rsquo;t always find all three. The point is to find the questions your writing actually needs to answer.</Note>
          <NextBtn label="Let&rsquo;s look at the first one" onClick={next} testid="lesson-next" />
        </div>
      );
      case "asked": return (
        <div className="space-y-5">
          <Kicker n="1">The question being asked</Kicker>
          <Lead className="text-stone-800"><span className="font-medium">The first question is:</span> What does the assignment actually ask you to answer?</Lead>
          <Lead>Usually, this is easy. If the assignment is:</Lead>
          <SourceCard>&ldquo;Explain the benefits of exercise.&rdquo;</SourceCard>
          <Lead>the question being asked is simply:</Lead>
          <AskedCard>What are the benefits of exercise?</AskedCard>
          <Note>That&rsquo;s it. You&rsquo;ve turned the assignment into the question it asks you to answer.</Note>
          <Reveal>But remember &mdash; it&rsquo;s not always this easy.</Reveal>
          <Lead>Remember our first example?</Lead>
          <div className="ml-2 max-w-xl" data-testid="ua-reminder">
            <SourceCard>{DEMOC}</SourceCard>
            <Connector label="asked 4 questions at once" />
            <div className="space-y-1.5">
              <AskedCard>What is the difference between democracy and autocracy?</AskedCard>
              <AskedCard>How are decisions made in a democracy?</AskedCard>
              <AskedCard>How are decisions made in an autocracy?</AskedCard>
              <AskedCard>How do those ways of making decisions differ?</AskedCard>
            </div>
          </div>
          <Lead>A longer or more complicated assignment may ask several questions at once.</Lead>
          <Reveal>If you miss one, you may miss part of the assignment.</Reveal>
          <NextBtn label="Next — the second kind" onClick={next} testid="lesson-next" />
        </div>
      );
      case "tfg": return (
        <div className="space-y-5">
          <Kicker n="2">Questions about what is taken for granted</Kicker>
          <Lead className="text-stone-800"><span className="font-medium">The second type of question is:</span> What does the assignment assume <span className="font-semibold">you</span> understand that your <span className="font-semibold">reader</span> might not?</Lead>
          <Lead>You&rsquo;ve found what your assignment actually asks. But that may not be enough &mdash; an assignment can take important things for granted. For example, look at this question:</Lead>
          <AskedCard>What is the difference between democracy and autocracy?</AskedCard>
          <Lead>The question directly asks about the <span className="font-medium">difference</span>. But notice what it <span className="font-medium">doesn&rsquo;t</span> ask:</Lead>
          <AssumedCard>
            <p>What is democracy?</p>
            <p>What is autocracy?</p>
          </AssumedCard>
          <Lead>Why doesn&rsquo;t it ask those? Because the assignment assumes <span className="font-semibold">you</span> understand what democracy and autocracy are. But that doesn&rsquo;t mean your <span className="font-semibold">reader</span> does.</Lead>
          <Reveal>Even though the assignment doesn&rsquo;t explicitly ask, you may have to answer these questions for your reader.</Reveal>
          <Note>You don&rsquo;t have to explain everything an assignment takes for granted. You have to decide what your reader needs in order to understand your answer.</Note>
          <Cue>What does the assignment assume I understand that my reader might not?</Cue>
          <NextBtn label="Next — the third kind" onClick={next} testid="lesson-next" />
        </div>
      );
      case "needed": return (
        <div className="space-y-5">
          <Kicker n="3">Questions about what is needed to fully answer</Kicker>
          <Lead className="text-stone-800"><span className="font-medium">The third type of question is:</span> What else do you need to address to fully answer what the assignment asks?</Lead>
          <Lead>This one is a little harder. Sometimes you can answer the question that was <span className="font-medium">asked</span> and still leave something important unexplained. These aren&rsquo;t simply things the assignment takes for granted &mdash; they&rsquo;re questions you discover when you ask:</Lead>
          <Cue>What would a complete answer have to explain?</Cue>
          <Lead className="font-medium text-stone-800">Let&rsquo;s look at an example.</Lead>
          <SourceCard>&ldquo;How was Frankl able to create meaning in the concentration camps?&rdquo;</SourceCard>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#8C3A2A]">First · 1 · Asked</p>
          <AskedCard>How was Frankl able to create meaning in the concentration camps?</AskedCard>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-700">Second · 2 · Taken for granted</p>
          <AssumedCard>
            <p>Who was Frankl?</p>
            <p>What were the concentration camps?</p>
          </AssumedCard>
          <Note>Those questions provide background the assignment takes for granted. But they still don&rsquo;t tell us everything a complete answer has to explain.</Note>
          <div className="rounded-md bg-stone-50 border border-stone-200 px-4 py-3 max-w-xl" data-testid="ua-hypothetical">
            <p className="text-[11px] uppercase tracking-[0.14em] text-stone-400 mb-1">Now suppose a student answers</p>
            <p className="font-serif italic text-[16px] text-stone-700">&ldquo;Frankl created meaning through love, purposeful work, and the way he responded to suffering.&rdquo;</p>
          </div>
          <Lead>That gives us an answer &mdash; the student has identified things that gave Frankl meaning.</Lead>
          <Cue>But has the student fully explained HOW they created meaning?</Cue>
          <p className="font-serif text-[18px] text-stone-900">Not yet.</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal-700">3 · Needed to fully answer</p>
          <NeededCard>
            <p>How did love give Frankl a sense of meaning?</p>
            <p>How did purposeful work create meaning?</p>
            <p>How did his response to suffering create meaning?</p>
          </NeededCard>
          <Note>The assignment never directly asked these questions. They are not just background information. But without answering them, the student has only <span className="italic">named</span> sources of meaning &mdash; not fully explained <span className="italic">how</span> Frankl created meaning.</Note>
          <Cue>Could I answer what was asked and still leave something important unexplained?</Cue>
          <Lead>If yes, you may have found a question that is <span className="text-teal-700 font-medium">needed to fully answer</span>.</Lead>
          <NextBtn label="Try it with my assignment" onClick={next} testid="lesson-done" />
        </div>
      );
      default: return null;
    }
  };

  return (
    <section data-testid="understanding-lesson" className="rounded-2xl border border-stone-200 bg-white p-6 sm:p-8">
      <div className="flex items-center mb-5 min-h-[20px]">
        {idx > 0 ? (
          <button data-testid="lesson-back" onClick={back}
            className="inline-flex items-center gap-1 text-[12.5px] text-stone-500 hover:text-[#8C3A2A] transition-colors">
            <ArrowRight className="h-3.5 w-3.5 rotate-180" /> Back
          </button>
        ) : <span />}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}
          data-testid={`lesson-screen-${id}`}>
          {renderScreen(id)}
        </motion.div>
      </AnimatePresence>
    </section>
  );
}


// Renders the prompt with a phrase TWS is directing attention to visually emphasized (§7).
function HighlightedPrompt({ text, hl }) {
  if (!hl) return <>{text}</>;
  const i = text.toLowerCase().indexOf(hl.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark data-testid="prompt-highlight" className="bg-amber-200/70 text-stone-900 rounded px-0.5">{text.slice(i, i + hl.length)}</mark>
      {text.slice(i + hl.length)}
    </>
  );
}
